// src/account-refusal.ts
var AccountRefusedError = class extends Error {
  code;
  request;
  constructor(request, code) {
    super(`${request} refused: forbidden (${code})`);
    this.name = "AccountRefusedError";
    this.code = code;
    this.request = request;
  }
};
function readForbidden(raw) {
  let body;
  try {
    body = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof body !== "object" || body === null) return null;
  const { error, code } = body;
  if (error !== "forbidden") return null;
  if (code === void 0) return {};
  if (typeof code !== "string")
    throw new Error(`403 refusal carries a non-string code: ${JSON.stringify(code)}`);
  return { code };
}

// src/adopt.ts
import { UNWRITTEN_FIELD } from "@jbroll/rowboat-shared";

// src/db-factory.ts
import {
  computeCopyTarget as computeCopyTarget2,
  conversionFn,
  DEFAULT_EPOCH_MS,
  hexPack
} from "@jbroll/rowboat-shared";

// src/capped-columns.ts
var byVersion = (migrations) => [...migrations].sort((a, b) => a.toVersion - b.toVersion);
function describe(step) {
  switch (step.type) {
    case "addTable":
      return `addTable ${step.def.name}`;
    case "destroyTable":
      return `destroyTable ${step.name}`;
    case "renameTable":
      return `renameTable ${step.from} -> ${step.to}`;
    case "addColumn":
      return `addColumn ${step.table}.${step.column.name}`;
    case "renameColumn":
      return `renameColumn ${step.table}.${step.from} -> ${step.to}`;
    default:
      return `${step.type} on ${step.table}`;
  }
}
function broken(step, why) {
  return new Error(`rowboat: migration step ${describe(step)} ${why}`);
}
function tableIn(state, name, step) {
  const t = state.get(name);
  if (t === void 0) throw broken(step, `names table '${name}', which the chain does not hold`);
  return t;
}
function renameColumn(t, from, to, step) {
  const c = t.cols.get(from);
  if (c === void 0) throw broken(step, `names column '${from}', which the table does not hold`);
  if (t.cols.has(to)) throw broken(step, `renames onto existing column '${to}'`);
  t.cols.delete(from);
  t.cols.set(to, { ...c, name: to });
}
function renameTable(state, from, to, step) {
  const t = tableIn(state, from, step);
  if (state.has(to)) throw broken(step, `renames onto existing table '${to}'`);
  state.delete(from);
  state.set(to, t);
}
function setIndexed(t, step, was) {
  const c = t.cols.get(step.column);
  if (c === void 0)
    throw broken(step, `names column '${step.column}', which the table does not hold`);
  if (Boolean(c.isIndexed) !== was)
    throw broken(step, `finds column '${step.column}' ${was ? "not indexed" : "already indexed"}`);
  t.cols.set(step.column, { ...c, isIndexed: !was });
}
function setScope(t, step, from, to) {
  const c = t.cols.get(step.column);
  if (c === void 0)
    throw broken(step, `names column '${step.column}', which the table does not hold`);
  if (Boolean(c.scope) !== Boolean(from.scope))
    throw broken(step, `finds column '${step.column}' ${c.scope ? "scoped" : "not scoped"}`);
  const next = { ...c };
  if (to.scope) next.scope = true;
  else delete next.scope;
  t.cols.set(step.column, next);
}
function previousMeta(step) {
  if (step.previous === void 0)
    throw new Error(
      `rowboat: setColumnMeta ${step.table}.${step.column} has no previous column meta; regenerate the migration history`
    );
  return step.previous;
}
function checkCopyColumns(t, step) {
  for (const col of [step.target.col, ...step.sources.map((s) => s.col)])
    if (!t.cols.has(col))
      throw broken(step, `names column '${col}', which the table does not hold`);
}
function unknownStep(step) {
  return new Error(`rowboat: unrecognized migration step type ${step.type}`);
}
function applyStep(state, step, creates) {
  switch (step.type) {
    case "addColumn": {
      const t = tableIn(state, step.table, step);
      if (t.cols.has(step.column.name))
        throw broken(step, `adds existing column '${step.column.name}'`);
      t.cols.set(step.column.name, step.column);
      return;
    }
    case "addTable":
      if (state.has(step.def.name)) throw broken(step, `adds existing table '${step.def.name}'`);
      state.set(step.def.name, {
        cols: new Map(step.def.columns.map((c) => [c.name, c])),
        current: creates.get(step)
      });
      return;
    case "destroyTable":
      tableIn(state, step.name, step);
      state.delete(step.name);
      return;
    case "dropColumn": {
      const t = tableIn(state, step.table, step);
      if (!t.cols.delete(step.column))
        throw broken(step, `drops column '${step.column}', which the table does not hold`);
      return;
    }
    case "renameColumn":
      renameColumn(tableIn(state, step.table, step), step.from, step.to, step);
      return;
    case "renameTable":
      renameTable(state, step.from, step.to, step);
      return;
    case "addIndex":
    case "dropIndex":
      setIndexed(tableIn(state, step.table, step), step, step.type === "dropIndex");
      return;
    case "setColumnMeta":
      setScope(tableIn(state, step.table, step), step, previousMeta(step), step.meta);
      return;
    case "copyFromExpression":
      checkCopyColumns(tableIn(state, step.table, step), step);
      return;
    default:
      throw unknownStep(step);
  }
}
function unapplyStep(start, step, version, owed) {
  const { state } = start;
  switch (step.type) {
    case "addColumn": {
      const t = tableIn(state, step.table, step);
      if (!t.cols.delete(step.column.name))
        throw broken(step, `adds column '${step.column.name}', which the next manifest lacks`);
      return;
    }
    case "addTable": {
      const t = tableIn(state, step.def.name, step);
      if (t.current !== void 0) {
        start.creates.set(step, t.current);
        start.addedAt.set(t.current, version);
      }
      state.delete(step.def.name);
      return;
    }
    case "destroyTable":
      if (step.dropped === void 0)
        throw new Error(
          `rowboat: destroyTable ${step.name} has no dropped table definition; regenerate the migration history`
        );
      if (step.dropped.name !== step.name)
        throw broken(step, `carries the definition of table '${step.dropped.name}'`);
      if (state.has(step.name))
        throw broken(step, `destroys '${step.name}', which still exists after it`);
      state.set(step.name, { cols: new Map(step.dropped.columns.map((c) => [c.name, c])) });
      return;
    case "dropColumn": {
      if (step.dropped === void 0)
        throw new Error(
          `rowboat: dropColumn ${step.table}.${step.column} has no dropped column shape; regenerate the migration history`
        );
      const t = tableIn(state, step.table, step);
      if (t.cols.has(step.column))
        throw broken(step, `drops '${step.column}', which still exists after it`);
      t.cols.set(step.column, { ...step.dropped, isIndexed: false });
      if (step.dropped.isIndexed) {
        const waiting = owed.get(t) ?? /* @__PURE__ */ new Map();
        waiting.set(step.column, step);
        owed.set(t, waiting);
      }
      return;
    }
    case "renameColumn":
      renameColumn(tableIn(state, step.table, step), step.to, step.from, step);
      return;
    case "renameTable":
      renameTable(state, step.to, step.from, step);
      return;
    case "addIndex":
    case "dropIndex":
      setIndexed(tableIn(state, step.table, step), step, step.type === "addIndex");
      if (step.type === "dropIndex")
        owed.get(tableIn(state, step.table, step))?.delete(step.column);
      return;
    case "setColumnMeta":
      setScope(tableIn(state, step.table, step), step, step.meta, previousMeta(step));
      return;
    case "copyFromExpression":
      checkCopyColumns(tableIn(state, step.table, step), step);
      return;
    default:
      throw unknownStep(step);
  }
}
function assertHistoryMatchesManifest(tables, start, migrations) {
  const state = cloneChain(start.state);
  for (const m of byVersion(migrations))
    for (const step of m.steps) applyStep(state, step, start.creates);
  const disagree = (what) => new Error(`rowboat: the manifest disagrees with its migration history: ${what}`);
  const held = new Set(state.keys());
  for (const t of tables) {
    const replayed = state.get(t.name);
    if (replayed === void 0)
      throw disagree(`table '${t.name}' is not a table the history produces`);
    held.delete(t.name);
    const names = new Set(t.columns.map((c) => c.name));
    for (const c of t.columns) {
      const h = replayed.cols.get(c.name);
      if (h === void 0) throw disagree(`${t.name}.${c.name} is missing after the history`);
      if (h.type !== c.type)
        throw disagree(
          `${t.name}.${c.name} has type ${c.type} in the manifest, ${h.type} in the history`
        );
      if (Boolean(h.isIndexed) !== Boolean(c.isIndexed))
        throw disagree(
          `${t.name}.${c.name} has isIndexed ${Boolean(c.isIndexed)} in the manifest, ${Boolean(h.isIndexed)} in the history`
        );
      if (Boolean(h.scope) !== Boolean(c.scope))
        throw disagree(
          `${t.name}.${c.name} has scope ${Boolean(c.scope)} in the manifest, ${Boolean(h.scope)} in the history`
        );
    }
    for (const name of replayed.cols.keys())
      if (!names.has(name)) throw disagree(`${t.name}.${name} is not in the manifest`);
  }
  const extra = [...held][0];
  if (extra !== void 0) throw disagree(`table '${extra}' is not in the manifest`);
}
function unwindChain(tables, migrations) {
  const start = {
    state: new Map(
      tables.map((t) => [
        t.name,
        { cols: new Map(t.columns.map((c) => [c.name, c])), current: t.name }
      ])
    ),
    creates: /* @__PURE__ */ new Map(),
    addedAt: /* @__PURE__ */ new Map()
  };
  const owed = /* @__PURE__ */ new Map();
  for (const m of byVersion(migrations).reverse())
    for (const step of [...m.steps].reverse()) unapplyStep(start, step, m.toVersion, owed);
  for (const waiting of owed.values())
    for (const step of waiting.values())
      if (step.type === "dropColumn")
        throw new Error(
          `rowboat: migration step dropColumn ${step.table}.${step.column} drops an indexed column that no earlier dropIndex releases`
        );
  assertHistoryMatchesManifest(tables, start, migrations);
  return start;
}
function cloneChain(state) {
  const out = /* @__PURE__ */ new Map();
  for (const [name, t] of state) out.set(name, { ...t, cols: new Map(t.cols) });
  return out;
}
function chainAtCap(start, migrations, maxVersion) {
  const state = cloneChain(start.state);
  for (const m of byVersion(migrations)) {
    if (m.toVersion > maxVersion) break;
    for (const step of m.steps) applyStep(state, step, start.creates);
  }
  const out = {
    columns: /* @__PURE__ */ new Map(),
    storeTables: /* @__PURE__ */ new Map(),
    tablesAboveCap: /* @__PURE__ */ new Map()
  };
  for (const [name, t] of state) {
    out.columns.set(name, t.cols);
    if (t.current !== void 0) out.storeTables.set(t.current, name);
  }
  for (const [table2, version] of start.addedAt)
    if (version > maxVersion) out.tablesAboveCap.set(table2, version);
  return out;
}
function columnsOfType(columns, type) {
  const out = /* @__PURE__ */ new Map();
  for (const [table2, cols] of columns) {
    const names = [...cols.values()].filter((c) => c.type === type).map((c) => c.name);
    if (names.length > 0) out.set(table2, new Set(names));
  }
  return out;
}

// src/column-defaults.ts
import { UNWRITTEN_FIELD as UNWRITTEN } from "@jbroll/rowboat-shared";
function storedDefault(col) {
  if (col.isOptional) return null;
  if (col.type === "set") return "[]";
  if (col.type === "text" || col.type === "json") return "";
  return 0;
}
function defaultsByTable(columns) {
  const out = /* @__PURE__ */ new Map();
  for (const [table2, cols] of columns) {
    const defaults = /* @__PURE__ */ new Map();
    for (const [name, col] of cols) defaults.set(name, storedDefault(col));
    out.set(table2, defaults);
  }
  return out;
}
function unwrittenOf(row) {
  const v = row[UNWRITTEN];
  if (v === void 0) return [];
  if (!Array.isArray(v) || v.some((c) => typeof c !== "string"))
    throw new Error(`${UNWRITTEN} on row ${row.id} is not a list of column names`);
  return v;
}
function setUnwritten(row, cols) {
  if (cols.length === 0) delete row[UNWRITTEN];
  else row[UNWRITTEN] = [...cols];
}
function renameUnwritten(row, from, to) {
  setUnwritten(
    row,
    unwrittenOf(row).map((c) => c === from ? to : c)
  );
}
function clearUnwritten(row, col) {
  setUnwritten(
    row,
    unwrittenOf(row).filter((c) => c !== col)
  );
}
function addColumnDefault(row, col) {
  if (row[col.name] !== void 0)
    throw new Error(`addColumn ${col.name}: row ${row.id} already holds the column`);
  row[col.name] = storedDefault(col);
  setUnwritten(row, [...unwrittenOf(row), col.name]);
}
function clearWritten(row, keys) {
  const written = /* @__PURE__ */ new Set();
  for (const k of keys) written.add(k.split(".")[0]);
  if (row[UNWRITTEN] === void 0) return;
  setUnwritten(
    row,
    unwrittenOf(row).filter((c) => !written.has(c))
  );
}
function fillDefaults(row, defaults) {
  const filled = unwrittenOf(row);
  for (const [col, value] of defaults) {
    if (row[col] !== void 0) continue;
    row[col] = value;
    filled.push(col);
  }
  setUnwritten(row, filled);
}
function unwrittenAtDefault(row, defaults) {
  return unwrittenOf(row).filter((col) => {
    if (!defaults.has(col)) throw new Error(`${UNWRITTEN} on row ${row.id} names unknown ${col}`);
    return row[col] === defaults.get(col);
  });
}

// src/cross-tab.ts
function parseSignal(data) {
  if (typeof data === "object" && data !== null && Array.isArray(data.tables) && data.tables.every((t) => typeof t === "string")) {
    return data.tables;
  }
  throw new Error(`rowboat cross-tab: malformed signal ${JSON.stringify(data)}`);
}
var CrossTabChannel = class {
  _channel;
  _pending = /* @__PURE__ */ new Set();
  _scheduled = false;
  _closed = false;
  constructor(dbName, onRemote) {
    this._channel = new BroadcastChannel(`rowboat:${dbName}`);
    this._channel.onmessage = (ev) => onRemote(parseSignal(ev.data));
    this._channel.unref?.();
  }
  // Coalesce a burst (e.g. a 100-row batch loop) into a single message flushed on the next microtask.
  broadcast(table2) {
    if (this._closed) return;
    this._pending.add(table2);
    if (!this._scheduled) {
      this._scheduled = true;
      queueMicrotask(() => this._flush());
    }
  }
  _flush() {
    this._scheduled = false;
    if (this._closed || this._pending.size === 0) return;
    const tables = [...this._pending];
    this._pending.clear();
    this._channel.postMessage({ tables });
  }
  close() {
    this._closed = true;
    this._pending.clear();
    this._channel.close();
  }
};
function createCrossTabChannel(dbName, onRemote, enabled) {
  if (!enabled || typeof BroadcastChannel === "undefined") return void 0;
  return new CrossTabChannel(dbName, onRemote);
}

// src/db.ts
import { formatHlc as formatHlc3, hlcNext as hlcNext2 } from "@jbroll/rowboat-shared";

// src/db-crud.ts
import { isAdd as isAdd2, isInc as isInc2, isRemove as isRemove2, sortSetElements as sortSetElements2 } from "@jbroll/rowboat-shared";

// src/db-changes.ts
import {
  assertSingleSentinel,
  cellDelete,
  cellPut,
  deepSet,
  deepUnset,
  getPathEntry,
  hexCompare,
  isAdd,
  isInc,
  isRemove,
  readCell,
  sortSetElements,
  writeCell
} from "@jbroll/rowboat-shared";

// src/json-cell.ts
function encodeJsonValue(table2, id, col, v) {
  const refuse = (why) => new Error(`${table2}/${id} column ${col}: ${why}`);
  if (typeof v === "number" && !Number.isFinite(v)) throw refuse(`${v} is not a JSON number`);
  if (typeof v === "object" && v !== null && !Array.isArray(v)) {
    const proto = Object.getPrototypeOf(v);
    if (proto !== Object.prototype && proto !== null)
      throw refuse("a json value must be a plain object or an array");
  }
  let text;
  try {
    text = JSON.stringify(v);
  } catch (e) {
    throw refuse(`JSON cannot hold this value (${e.message})`);
  }
  if (text === void 0) throw refuse(`JSON cannot hold a ${typeof v}`);
  return text;
}
function storedCell(table2, id, col, stored) {
  if (stored === void 0 || stored === null) return null;
  if (typeof stored !== "string")
    throw new Error(
      `${table2}/${String(id)} column ${col}: a stored ${typeof stored}, not JSON text`
    );
  return stored;
}

// src/db-changes.ts
function assertKnownColumn(effectiveCols, table2, key, verb) {
  const cols = effectiveCols.get(table2);
  if (cols === void 0)
    throw new Error(`${verb}: table ${table2} has no known columns at this store's schema version`);
  if (cols.has(key)) return;
  throw new Error(`${verb}: "${key}" is not a column of ${table2} at this store's schema version`);
}
function assertIndependentPaths(table2, id, changes) {
  const keys = new Set(Object.keys(changes).filter((k) => k.includes(".")));
  for (const key of keys) {
    for (let dot = key.lastIndexOf("."); dot > 0; dot = key.lastIndexOf(".", dot - 1)) {
      const outer = key.slice(0, dot);
      if (keys.has(outer))
        throw new Error(
          `update ${table2}/${String(id)}: "${outer}" and "${key}" name a path and a path inside it; write them in separate calls`
        );
    }
  }
}
function applyChanges(table2, jsonColSet, counterColSet, setColSet, existing, changes, stamp, effectiveCols) {
  const plainChanges = {};
  const pathGroups = /* @__PURE__ */ new Map();
  const opChanges = {};
  const updatedRow = { ...existing };
  for (const [key, value] of Object.entries(changes)) {
    if (counterColSet.has(key) || setColSet.has(key)) assertSingleSentinel(value);
    if (counterColSet.has(key)) {
      if (!isInc(value)) throw new Error(`counter column "${key}" accepts only rb.inc(n)`);
      const delta = value.__rbInc;
      opChanges[key] = delta;
      const base = key in existing ? existing[key] : 0;
      if (typeof base !== "number")
        throw new Error(
          `counter column ${table2}.${key} holds a non-number (${typeof base}); refusing to inc a corrupt value`
        );
      updatedRow[key] = base + delta;
      continue;
    }
    if (setColSet.has(key)) {
      if (!isAdd(value) && !isRemove(value))
        throw new Error(`set column "${key}" accepts only rb.add/rb.remove`);
      let cur;
      if (key in existing) {
        const stored = existing[key];
        if (typeof stored !== "string")
          throw new Error(
            `set column ${table2}.${key} holds a non-string (${typeof stored}); refusing to add/remove on a corrupt value`
          );
        const parsed = JSON.parse(stored);
        if (!Array.isArray(parsed))
          throw new Error(
            `set column ${table2}.${key} stored value does not parse to an array; refusing to add/remove on a corrupt value`
          );
        cur = new Set(parsed);
      } else {
        cur = /* @__PURE__ */ new Set();
      }
      const op = {};
      if (isAdd(value)) {
        for (const e of value.__rbAdd) cur.add(e);
        op.add = value.__rbAdd;
      } else {
        for (const e of value.__rbRemove) cur.delete(e);
        op.remove = value.__rbRemove;
      }
      opChanges[key] = op;
      updatedRow[key] = JSON.stringify(sortSetElements([...cur]));
      continue;
    }
    const dot = key.indexOf(".");
    if (dot !== -1) {
      const col = key.slice(0, dot);
      if (!jsonColSet.has(col))
        throw new Error(`update: "${col}" is not a json column \u2014 dotted paths require type:"json"`);
      if (!pathGroups.has(col)) pathGroups.set(col, /* @__PURE__ */ new Map());
      pathGroups.get(col)?.set(key.slice(dot + 1), value);
    } else {
      assertKnownColumn(effectiveCols, table2, key, "update");
      plainChanges[key] = value;
    }
  }
  Object.assign(opChanges, plainChanges);
  Object.assign(updatedRow, plainChanges);
  const pathClocks = {};
  for (const col of jsonColSet) {
    const v = updatedRow[col];
    if (!(col in plainChanges) || v === null || v === void 0) continue;
    updatedRow[col] = encodeJsonValue(table2, existing.id, col, v);
    if (typeof v !== "object") opChanges[col] = updatedRow[col];
  }
  for (const [col, paths] of pathGroups) {
    const wholeObjectWrite = col in plainChanges;
    const cell = readCell(storedCell(table2, existing.id, col, updatedRow[col]));
    const partialBlob = {};
    for (const [path, value] of paths) {
      deepSet(partialBlob, path, value);
      if (value === void 0) cellDelete(cell, path);
      else cellPut(cell, path, value);
      if (!wholeObjectWrite) pathClocks[`${col}.${path}`] = stamp;
    }
    updatedRow[col] = writeCell(cell);
    opChanges[col] = !wholeObjectWrite ? partialBlob : typeof cell.v === "object" && cell.v !== null ? cell.v : writeCell(cell);
  }
  if (Object.keys(pathClocks).length > 0) opChanges.__clocks = pathClocks;
  return { opChanges, updatedRow };
}
function hasPathClocks(clocks, col) {
  const prefix = `${col}.`;
  for (const k of Object.keys(clocks)) if (k.startsWith(prefix)) return true;
  return false;
}
function setElements(op) {
  const o = op;
  return [...o.add ?? [], ...o.remove ?? []];
}
function laterCoversEarlier(earlier, later, earlierClocks, sets) {
  for (const [col, v] of Object.entries(earlier)) {
    if (col === "__clocks" || hasPathClocks(earlierClocks, col)) continue;
    if (!(col in later)) return false;
    if (sets.has(col)) {
      const covered = new Set(setElements(later[col]));
      if (setElements(v).some((e) => !covered.has(e))) return false;
    }
  }
  return true;
}
function mergeOpChanges(earlier, later, sets) {
  const earlierClocks = earlier.__clocks ?? {};
  const laterClocks = later.__clocks ?? {};
  for (const col of Object.keys(later)) {
    if (col === "__clocks" || !(col in earlier)) continue;
    if (hasPathClocks(earlierClocks, col) !== hasPathClocks(laterClocks, col)) return null;
  }
  for (const lk of Object.keys(laterClocks))
    for (const ek of Object.keys(earlierClocks))
      if (ek === lk || ek.startsWith(`${lk}.`) || lk.startsWith(`${ek}.`)) return null;
  if (!laterCoversEarlier(earlier, later, earlierClocks, sets)) return null;
  const merged = { ...earlier };
  for (const [col, v] of Object.entries(later)) {
    if (col === "__clocks") continue;
    const prev = merged[col];
    if (hasPathClocks(laterClocks, col) && prev !== null && typeof prev === "object" && v !== null && typeof v === "object") {
      const copy = JSON.parse(JSON.stringify(prev));
      const partial = v;
      for (const key of Object.keys(laterClocks)) {
        if (!key.startsWith(`${col}.`)) continue;
        const path = key.slice(col.length + 1);
        const { found, value } = getPathEntry(partial, path);
        if (found) deepSet(copy, path, JSON.parse(JSON.stringify(value)));
        else deepUnset(copy, path);
      }
      merged[col] = copy;
    } else {
      merged[col] = v;
    }
  }
  delete merged.__clocks;
  const clocks = { ...earlierClocks, ...laterClocks };
  if (Object.keys(clocks).length > 0) merged.__clocks = clocks;
  return merged;
}
function underAny(paths, path) {
  if (paths === void 0) return false;
  for (const q of paths) if (path === q || path.startsWith(`${q}.`)) return true;
  return false;
}
function pathCovered(w, col, path, isSet) {
  return underAny(w.paths.get(col), path) || !isSet && underAny(w.deletes.get(col), path);
}
function addTo(m, k, v) {
  const s = m.get(k);
  if (s) s.add(v);
  else m.set(k, /* @__PURE__ */ new Set([v]));
}
function stripOp(changes, sets, w) {
  const clocks = changes.__clocks ?? {};
  for (const k of Object.keys(clocks)) {
    const dot = k.indexOf(".");
    if (dot <= 0 || !(k.slice(0, dot) in changes))
      throw new Error(`coalesce: path clock ${k} has no column in its op`);
  }
  const out = {};
  const kept = {};
  let stripped = false;
  for (const [col, v] of Object.entries(changes)) {
    if (col === "__clocks") continue;
    if (sets.has(col)) {
      const s = v;
      const seen = w.elems.get(col);
      const add2 = (s.add ?? []).filter((e) => !seen?.has(e));
      const remove3 = (s.remove ?? []).filter((e) => !seen?.has(e));
      for (const e of setElements(v)) addTo(w.elems, col, e);
      if (add2.length + remove3.length < setElements(v).length) stripped = true;
      if (add2.length + remove3.length === 0) continue;
      out[col] = { ...add2.length > 0 && { add: add2 }, ...remove3.length > 0 && { remove: remove3 } };
    } else if (hasPathClocks(clocks, col)) {
      const prefix = `${col}.`;
      const mine = Object.keys(clocks).filter((k) => k.startsWith(prefix));
      const blobOf = v;
      const isSet = (k) => getPathEntry(blobOf, k.slice(prefix.length)).found;
      const gone = mine.filter((k) => pathCovered(w, col, k.slice(prefix.length), isSet(k)));
      for (const k of mine) {
        const path = k.slice(prefix.length);
        addTo(isSet(k) || !path.includes(".") ? w.paths : w.deletes, col, path);
      }
      if (gone.length > 0) stripped = true;
      const left = mine.filter((k) => !gone.includes(k));
      if (left.length === 0) continue;
      const blob = JSON.parse(JSON.stringify(v));
      for (const k of gone)
        if (!left.some((a) => k.startsWith(`${a}.`))) deepUnset(blob, k.slice(prefix.length));
      for (const k of left) kept[k] = clocks[k];
      out[col] = blob;
    } else {
      const covered = w.whole.has(col);
      w.whole.add(col);
      if (covered) {
        stripped = true;
        continue;
      }
      out[col] = v;
    }
  }
  if (Object.keys(kept).length > 0) out.__clocks = kept;
  return stripped && Object.keys(out).length === 0 ? null : out;
}
function coalescePendingOps(ops, pendingCreates2, kinds) {
  const rowKey3 = (table2, id) => `${table2.length}:${table2}:${id}`;
  const createHlcs = /* @__PURE__ */ new Map();
  if (pendingCreates2) {
    for (const [table2, rows] of pendingCreates2) {
      for (const row of rows) {
        if (typeof row.__localHlc === "string")
          createHlcs.set(rowKey3(table2, row.id), row.__localHlc);
      }
    }
  }
  const groups = /* @__PURE__ */ new Map();
  for (const op of ops) {
    const k = rowKey3(op.table, op.id);
    const g = groups.get(k);
    if (g) g.push(op);
    else groups.set(k, [op]);
  }
  const empty = /* @__PURE__ */ new Set();
  const out = [];
  for (const [key, g] of groups) {
    g.sort((x, y) => hexCompare(x.hlc, y.hlc));
    const kind = kinds?.[g[0].table];
    const counters = kind?.counters ?? empty;
    const sets = kind?.sets ?? empty;
    const barrier = createHlcs.get(key);
    let run = [];
    const flushRun = () => {
      const w = {
        whole: /* @__PURE__ */ new Set(),
        paths: /* @__PURE__ */ new Map(),
        deletes: /* @__PURE__ */ new Map(),
        elems: /* @__PURE__ */ new Map()
      };
      const kept = [];
      for (let i = run.length - 1; i >= 0; i--) {
        const changes = stripOp(run[i].changes, sets, w);
        if (changes !== null) kept.unshift({ op: run[i].op, changes });
      }
      let acc = null;
      let accChanges = null;
      for (const { op, changes } of kept) {
        const merged = accChanges === null ? null : mergeOpChanges(accChanges, changes, sets);
        if (merged === null && acc !== null && accChanges !== null)
          out.push({ ...acc, changes: JSON.stringify(accChanges) });
        acc = op;
        accChanges = merged ?? changes;
      }
      if (acc !== null && accChanges !== null)
        out.push({ ...acc, changes: JSON.stringify(accChanges) });
      run = [];
    };
    for (const op of g) {
      if (op.isDelete === 1) {
        flushRun();
        out.push(op);
        continue;
      }
      const parsed = JSON.parse(op.changes);
      if (counters.size > 0 && Object.keys(parsed).some((k) => counters.has(k))) {
        flushRun();
        out.push(op);
        continue;
      }
      const last = run.at(-1);
      if (last !== void 0 && barrier !== void 0 && last.op.hlc < barrier && barrier < op.hlc)
        flushRun();
      run.push({ op, changes: parsed });
    }
    flushRun();
  }
  return out;
}

// src/push-chunk.ts
var PUSH_CHUNK_ROWS = 5e3;
function rowKey(table2, id) {
  return `${table2.length}:${table2}:${id}`;
}
function groupRoots(items) {
  const parent = items.map((_, i) => i);
  const find = (i) => {
    let r = i;
    while (parent[r] !== r) r = parent[r];
    while (parent[i] !== r) {
      const next = parent[i];
      parent[i] = r;
      i = next;
    }
    return r;
  };
  const owner = /* @__PURE__ */ new Map();
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    for (const id of item.ids) {
      const k = rowKey(item.table, id);
      const o = owner.get(k);
      if (o === void 0) {
        owner.set(k, i);
        continue;
      }
      const a = find(o);
      const b = find(i);
      if (a < b) parent[b] = a;
      else if (b < a) parent[a] = b;
    }
  }
  return items.map((_, i) => find(i));
}
function planPushChunk(entries, ops, budget) {
  const items = [
    ...entries.map((e) => ({
      kind: "create",
      stamp: e.stamp,
      key: e.createId,
      table: e.table,
      ids: e.ids,
      weight: e.ids.length
    })),
    ...ops.map((o) => ({
      kind: "op",
      stamp: o.hlc,
      key: o.opId,
      table: o.table,
      ids: [o.id],
      weight: 1
    }))
  ];
  items.sort(
    (a, b) => a.stamp === b.stamp ? a.kind === b.kind ? a.key - b.key : a.kind === "create" ? -1 : 1 : a.stamp < b.stamp ? -1 : 1
  );
  let total = 0;
  for (const item of items) total += item.weight;
  const taken = total <= budget ? items.map(() => true) : takeGroups(items, groupRoots(items), budget);
  const createIds = /* @__PURE__ */ new Set();
  const opIds = /* @__PURE__ */ new Set();
  let hasMore = false;
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (!taken[i]) hasMore = true;
    else if (item.kind === "create") createIds.add(item.key);
    else opIds.add(item.key);
  }
  return { createIds, opIds, hasMore };
}
function takeGroups(items, roots, budget) {
  const weight = /* @__PURE__ */ new Map();
  for (let i = 0; i < items.length; i++) {
    const r = roots[i];
    weight.set(r, (weight.get(r) ?? 0) + items[i].weight);
  }
  const chosen = /* @__PURE__ */ new Set();
  let claimed = 0;
  for (let i = 0; i < items.length; i++) {
    if (roots[i] !== i) continue;
    const w = weight.get(i);
    if (claimed > 0 && claimed + w > budget) break;
    chosen.add(i);
    claimed += w;
  }
  return roots.map((r) => chosen.has(r));
}
function oversizedPushError(changes, limit, budget) {
  let total = 0;
  let heaviest = { table: "", id: "", records: 0 };
  for (const [table2, { upserted }] of Object.entries(changes)) {
    const perRow = /* @__PURE__ */ new Map();
    for (const r of upserted) {
      const id = String(r.id);
      const n = (perRow.get(id) ?? 0) + 1;
      perRow.set(id, n);
      if (n > heaviest.records) heaviest = { table: table2, id, records: n };
      total++;
    }
  }
  if (heaviest.records > limit)
    return new Error(
      `Push refused: ${heaviest.table}/${heaviest.id} alone carries ${heaviest.records}, over the server's ${limit}-record push limit`
    );
  return new Error(
    `Push refused: the chunk carries ${total} records, over the server's ${limit}-record push limit (pushChunkRows ${budget})`
  );
}
function createEntryParts(entries, ops, size) {
  const opsPerRow = /* @__PURE__ */ new Map();
  for (const o of ops) {
    const k = rowKey(o.table, o.id);
    opsPerRow.set(k, (opsPerRow.get(k) ?? 0) + 1);
  }
  const entriesPerRow = /* @__PURE__ */ new Map();
  for (const e of entries)
    for (const id of new Set(e.ids)) {
      const k = rowKey(e.table, id);
      entriesPerRow.set(k, (entriesPerRow.get(k) ?? 0) + 1);
    }
  return entries.map((e) => {
    const parts = [];
    const shared = [];
    let part = [];
    let weight = 0;
    for (const id of e.ids) {
      const k = rowKey(e.table, id);
      if (entriesPerRow.get(k) > 1) {
        shared.push([id]);
        continue;
      }
      const w = 1 + (opsPerRow.get(k) ?? 0);
      if (part.length > 0 && weight + w > size) {
        parts.push(part);
        part = [];
        weight = 0;
      }
      part.push(id);
      weight += w;
    }
    if (part.length > 0) parts.push(part);
    return [...parts, ...shared];
  });
}

// src/db-crud.ts
async function create(table2, data) {
  await this._ensureOpen();
  const items = Array.isArray(data) ? data : [data];
  const jsonColSet = this._jsonCols.get(table2) ?? /* @__PURE__ */ new Set();
  const counterColSet = this._counterCols.get(table2) ?? /* @__PURE__ */ new Set();
  const setColSet = this._setCols.get(table2) ?? /* @__PURE__ */ new Set();
  const boolColSet = this._boolCols.get(table2) ?? /* @__PURE__ */ new Set();
  const defaults = this._colDefaults.get(table2);
  if (defaults === void 0)
    throw new Error(`create: ${table2} has no known columns at this store's version`);
  const rows = items.map((d) => {
    for (const key of Object.keys(d)) {
      if (key === "id") continue;
      assertKnownColumn(this._effectiveCols, table2, key, "create");
    }
    const row = { ...d, __deleted: 0 };
    for (const col of jsonColSet) {
      if (row[col] !== null && row[col] !== void 0)
        row[col] = encodeJsonValue(table2, row.id, col, row[col]);
    }
    for (const col of boolColSet) {
      if (col in row && typeof row[col] === "boolean") row[col] = row[col] ? 1 : 0;
    }
    for (const col of counterColSet) {
      if (!(col in row)) continue;
      if (isInc2(row[col]))
        throw new Error(
          `counter column ${table2}.${col}: create() takes a plain number, not rb.inc`
        );
      if (row[col] !== 0)
        throw new Error(
          `counter column ${table2}.${col} must start at 0; seed it with rb.inc() after create`
        );
    }
    for (const col of setColSet) {
      if (col in row) {
        if (isAdd2(row[col]) || isRemove2(row[col]))
          throw new Error(
            `set column ${table2}.${col}: create() takes a plain array, not rb.add/rb.remove`
          );
        if (Array.isArray(row[col]))
          row[col] = JSON.stringify(sortSetElements2(row[col]));
      }
    }
    fillDefaults(row, defaults);
    return row;
  });
  const ids = rows.map((r) => r.id);
  const seen = /* @__PURE__ */ new Set();
  for (const id of ids) {
    if (seen.has(id)) throw new Error(`create: ${table2}/${id} appears twice in one batch`);
    seen.add(id);
  }
  const scope = [this.table(table2), this._creates(), this._meta()];
  await this._store.transaction("rw", scope, async () => {
    const present = await this.table(table2).where(":id").anyOf(ids).primaryKeys();
    if (present.length > 0) {
      const existing = /* @__PURE__ */ new Map();
      const rows2 = await this.table(table2).bulkGet(present);
      for (const [i, row] of rows2.entries()) existing.set(present[i], row);
      for (const id of ids) {
        const row = existing.get(id);
        if (row !== void 0 && row.__deleted === 0)
          throw new Error(`create: ${table2}/${id} already exists; change it with update()`);
      }
    }
    await this._raiseToSharedClock();
    const hlc = this._nextHlc();
    await this._publishClock();
    for (const row of rows) row.__localHlc = hlc;
    await this.table(table2).bulkPut(rows);
    if (ids.length > 0) await this._creates().add({ table: table2, stamp: hlc, ids });
  });
  for (const row of rows) this._pendingCache.set(table2, row.id, row);
  if (rows.length === 1) {
    const row = rows[0];
    this._emitter.emit({ table: table2, type: "created", id: row.id, record: row });
  } else if (rows.length > 0) {
    this._emitter.emitBulk(table2);
  }
}
async function update(table2, id, changes) {
  const items = Array.isArray(id) ? id : [{ id, changes: changes ?? {} }];
  for (const it of items) assertIndependentPaths(table2, it.id, it.changes);
  await this._ensureOpen();
  const jsonColSet = this._jsonCols.get(table2) ?? /* @__PURE__ */ new Set();
  const counterColSet = this._counterCols.get(table2) ?? /* @__PURE__ */ new Set();
  const setColSet = this._setCols.get(table2) ?? /* @__PURE__ */ new Set();
  const boolColSet = this._boolCols.get(table2) ?? /* @__PURE__ */ new Set();
  if (boolColSet.size > 0) {
    for (const it of items) {
      for (const col of boolColSet) {
        if (col in it.changes && typeof it.changes[col] === "boolean")
          it.changes[col] = it.changes[col] ? 1 : 0;
      }
    }
  }
  const birthOrigin = counterColSet.size > 0 ? await this.getInstanceId() : void 0;
  const updatedRows = [];
  await this._store.transaction("rw", [this.table(table2), this._ops(), this._meta()], async () => {
    const existingRows = await this.table(table2).bulkGet(items.map((it) => it.id));
    await this._raiseToSharedClock();
    const ops = [];
    for (const [i, it] of items.entries()) {
      const existing = existingRows[i];
      if (!existing) throw new Error(`Row not found: ${table2}/${it.id}`);
      const hlc = this._nextHlc();
      const { opChanges, updatedRow } = applyChanges(
        table2,
        jsonColSet,
        counterColSet,
        setColSet,
        existing,
        it.changes,
        hlc,
        this._effectiveCols
      );
      clearWritten(updatedRow, Object.keys(it.changes));
      updatedRows.push(updatedRow);
      const writesCounter = counterColSet.size > 0 && Object.keys(opChanges).some((k) => counterColSet.has(k));
      ops.push({
        table: table2,
        id: it.id,
        hlc,
        changes: JSON.stringify(opChanges),
        isDelete: 0,
        ...writesCounter ? { origin: birthOrigin } : {}
      });
    }
    await this._publishClock();
    await this.table(table2).bulkPut(updatedRows);
    await this._ops().bulkAdd(ops);
  });
  for (let i = 0; i < updatedRows.length; i++) {
    const id2 = items[i].id;
    if (this._pendingCache.has(table2, id2)) this._pendingCache.set(table2, id2, updatedRows[i]);
  }
  if (updatedRows.length === 1) {
    this._emitter.emit({ table: table2, type: "updated", id: items[0].id, record: updatedRows[0] });
  } else if (updatedRows.length > 0) {
    this._emitter.emitBulk(table2);
  }
}
async function remove(table2, idOrIds) {
  await this._ensureOpen();
  const ids = Array.isArray(idOrIds) ? idOrIds : [idOrIds];
  if (ids.length === 0) return;
  await this._store.transaction("rw", [this.table(table2), this._ops(), this._meta()], async () => {
    await this._raiseToSharedClock();
    const ops = ids.map((id) => ({
      table: table2,
      id,
      hlc: this._nextHlc(),
      changes: "{}",
      isDelete: 1
    }));
    await this._publishClock();
    await this.table(table2).where("id").anyOf(ids).modify({ __deleted: 1 });
    await this._ops().bulkAdd(ops);
  });
  for (const id of ids) {
    this._pendingCache.delete(table2, id);
    this._emitter.emit({ table: table2, type: "deleted", id });
  }
}
async function hardDeleteAcked(byTable) {
  if (byTable.size === 0) return;
  const removed = [];
  const tables = [...byTable.keys()].map((t) => this.table(t));
  await this._store.transaction("rw", tables, async () => {
    for (const [table2, thresholds] of byTable) {
      const entries = [...thresholds];
      const rows = await this.table(table2).bulkGet(entries.map(([id]) => id));
      const toDelete = [];
      for (const [i, [id, threshold]] of entries.entries()) {
        const localHlc = rows[i]?.__localHlc;
        if (typeof localHlc === "string" && localHlc > threshold) continue;
        toDelete.push(id);
      }
      if (toDelete.length > 0) {
        await this.table(table2).bulkDelete(toDelete);
        for (const id of toDelete) removed.push([table2, id]);
      }
    }
  });
  for (const [table2, id] of removed) {
    this._pendingCache.delete(table2, id);
    this._emitter.emit({ table: table2, type: "deleted", id });
  }
}
async function pendingOps() {
  return this._ops().toArray();
}
async function clearOps(opIds) {
  await this._ops().bulkDelete(opIds);
}
function pendingCacheSize() {
  return this._pendingCache.size();
}
async function pendingCreateEntries() {
  return this._creates().toArray();
}
async function pendingCreates(entries) {
  entries ??= await this._creates().toArray();
  const createIds = [];
  const idsByTable = /* @__PURE__ */ new Map();
  for (const e of entries) {
    if (e.createId !== void 0) createIds.push(e.createId);
    let ids = idsByTable.get(e.table);
    if (!ids) {
      ids = /* @__PURE__ */ new Set();
      idsByTable.set(e.table, ids);
    }
    for (const id of e.ids) ids.add(id);
  }
  const creates = /* @__PURE__ */ new Map();
  for (const [table2, idSet] of idsByTable) {
    const pending = [];
    const misses = [];
    for (const id of idSet) {
      const cached = this._pendingCache.get(table2, id);
      if (cached) pending.push(cached);
      else misses.push(id);
    }
    if (misses.length > 0) {
      const rows = await this.table(table2).bulkGet(misses);
      for (const r of rows) {
        if (r !== void 0 && r.__deleted === 0 && typeof r.__localHlc === "string")
          pending.push(r);
      }
    }
    if (pending.length > 0) creates.set(table2, pending);
  }
  return { creates, createIds };
}
function unwrittenAtDefault2(table2, row) {
  const defaults = this._colDefaults.get(table2);
  if (defaults === void 0)
    throw new Error(`unwrittenAtDefault: ${table2} has no known columns at this store's version`);
  return unwrittenAtDefault(row, defaults);
}
async function clearCreates(createIds) {
  if (createIds.length === 0) return;
  const retired = await this._store.transaction(
    "rw",
    [this._creates()],
    () => deleteCreateEntries.call(this, createIds)
  );
  evictRetiredCreates.call(this, retired);
}
async function splitCreateEntries(entries, ops, size) {
  let total = ops.length;
  for (const e of entries) total += e.ids.length;
  if (total <= size) return [...entries];
  const parts = createEntryParts(entries, ops, size);
  if (parts.every((p) => p.length === 1)) return [...entries];
  const creates = this._creates();
  return this._store.transaction("rw", [creates], async () => {
    const out = [];
    for (const [i, e] of entries.entries()) {
      const [head, ...rest] = parts[i];
      if (rest.length === 0) {
        out.push(e);
        continue;
      }
      const createId = e.createId;
      if (createId === void 0)
        throw new Error("create side-log entry read back without its auto-increment key");
      const stored = await creates.get(createId);
      if (stored === void 0 || stored.stamp !== e.stamp || stored.table !== e.table || stored.ids.length !== e.ids.length || stored.ids.some((id, k) => id !== e.ids[k]))
        throw new Error(`create side-log entry ${createId} changed while the push was reading it`);
      const { createId: _key, ids: _ids, ...kept } = e;
      const first = { ...kept, createId, ids: head };
      await creates.put(first);
      out.push(first);
      for (const ids of rest) {
        const part = { ...kept, ids };
        out.push({ ...part, createId: await creates.add(part) });
      }
    }
    return out;
  });
}
async function deleteCreateEntries(createIds) {
  const sorted = [...createIds].sort((a, b) => a - b);
  const runs = [];
  for (const id of sorted) {
    const last = runs.at(-1);
    if (last !== void 0 && id <= last[1] + 1) last[1] = id;
    else runs.push([id, id]);
  }
  const retired = [];
  for (const [lo, hi] of runs) {
    const range = () => this._creates().where(":id").between(lo, hi, true, true);
    retired.push(...await range().toArray());
    await range().delete();
  }
  return retired;
}
function evictRetiredCreates(retired) {
  for (const e of retired) for (const id of e.ids) this._pendingCache.delete(e.table, id);
}

// src/db-quarantine.ts
var QUARANTINE_REASONS = [
  "unauthorized_group",
  "unknown_group",
  "invalid_scope",
  "schema_invalid",
  "clocks_invalid",
  "blob_missing"
];
var QUARANTINE = "quarantine:";
var DROPPED = "dropped:";
function rowKey2(table2, id) {
  return `${table2}:${id}`;
}
async function renameMetaRecords(meta, from, to) {
  for (const prefix of [QUARANTINE, DROPPED]) {
    const keyed = meta.where("key").startsWith(prefix + rowKey2(from, ""));
    const records = await keyed.toArray();
    await keyed.delete();
    await meta.bulkPut(
      records.map(({ value }) => {
        const ops = prefix === QUARANTINE ? readEntry(prefix + rowKey2(from, value.id), value).ops : void 0;
        const moved = {
          ...value,
          table: to,
          ...ops && { ops: ops.map((o) => ({ ...o, table: to })) }
        };
        return { key: prefix + rowKey2(to, value.id), value: moved };
      })
    );
  }
}
function readEntry(key, value) {
  const entry = value;
  if (!QUARANTINE_REASONS.includes(entry.reason))
    throw new Error(`${key} holds an entry with unknown reason ${JSON.stringify(entry.reason)}`);
  return entry;
}
async function quarantined() {
  const rows = await this._meta().where("key").startsWith(QUARANTINE).toArray();
  return rows.map((r) => readEntry(r.key, r.value)).sort((a, b) => a.at - b.at);
}
function quarantinedCount() {
  return this._meta().where("key").startsWith(QUARANTINE).count();
}
async function quarantinedOpsFor(table2, ids) {
  const found = await this._meta().bulkGet(ids.map((id) => QUARANTINE + rowKey2(table2, id)));
  const out = /* @__PURE__ */ new Map();
  for (const r of found) {
    if (r === void 0) continue;
    const e = readEntry(r.key, r.value);
    if (e.ops.length > 0) out.set(e.id, e.ops);
  }
  return out;
}
async function quarantinedCreatesFor(table2, ids) {
  const found = await this._meta().bulkGet(ids.map((id) => QUARANTINE + rowKey2(table2, id)));
  const out = /* @__PURE__ */ new Set();
  for (const r of found) {
    if (r === void 0) continue;
    const e = readEntry(r.key, r.value);
    if (e.table === table2 && e.pendingCreate !== void 0) out.add(e.id);
  }
  return out;
}
async function retryQuarantined() {
  const entries = await quarantined.call(this);
  if (entries.length === 0) return 0;
  await requeueQuarantined.call(this, entries);
  return entries.length;
}
async function putQuarantine(entries) {
  if (entries.length === 0) return;
  const keys = entries.map((e) => QUARANTINE + rowKey2(e.table, e.id));
  const existing = await this._meta().bulkGet(keys);
  const merged = entries.map((e, i) => {
    const held = existing[i];
    const prev = held === void 0 ? void 0 : readEntry(keys[i], held.value);
    if (prev === void 0) return e;
    return {
      ...e,
      // The earlier refusal's time is when this row stopped syncing.
      at: prev.at,
      ops: [...prev.ops, ...e.ops],
      pendingCreate: prev.pendingCreate ?? e.pendingCreate,
      suggestedGroup: e.suggestedGroup ?? prev.suggestedGroup
    };
  });
  await this._meta().bulkPut(merged.map((e, i) => ({ key: keys[i], value: e })));
}
function evictQuarantinedCreates(entries) {
  for (const e of entries)
    if (e.pendingCreate !== void 0) this._pendingCache.delete(e.table, e.id);
}
async function requeueQuarantined(entries) {
  if (entries.length === 0) return;
  await this._store.transaction("rw", [this._ops(), this._creates(), this._meta()], async () => {
    await putBackInOutbox.call(this, entries);
    await this._meta().bulkDelete(entries.map((e) => QUARANTINE + rowKey2(e.table, e.id)));
  });
}
async function putBackInOutbox(entries) {
  const ops = entries.flatMap((e) => e.ops);
  const byCreate = /* @__PURE__ */ new Map();
  for (const e of entries) {
    const pc = e.pendingCreate;
    if (pc === void 0) continue;
    const found = byCreate.get(pc.createId);
    if (found) found.ids.push(e.id);
    else
      byCreate.set(pc.createId, {
        table: e.table,
        stamp: pc.stamp,
        ids: [e.id]
      });
  }
  if (ops.length > 0) await this._ops().bulkAdd(ops);
  for (const [createId, c] of byCreate) {
    const held = await this._creates().get(createId);
    if (held === void 0) {
      await this._creates().add({ createId, ...c });
      continue;
    }
    if (held.table !== c.table || held.stamp !== c.stamp)
      throw new Error(
        `quarantine: create ${createId} is already queued for ${held.table} at ${held.stamp}, not ${c.table} at ${c.stamp}`
      );
    await this._creates().put({ ...held, ids: [.../* @__PURE__ */ new Set([...held.ids, ...c.ids])] });
  }
}
function refusedTables(o) {
  return [...new Set(o.dropped.map((d) => d.table))];
}
async function writeRefused(o) {
  await recordDropped.call(this, o.dropped);
  await putQuarantine.call(this, o.quarantine);
  const gone = [];
  if (o.dropped.length > 0) {
    const creates = await this._creates().toArray();
    for (const d of o.dropped) {
      const queued = await this._ops().where("id").equals(d.id).filter((op) => op.table === d.table).count() > 0 || creates.some((e) => e.table === d.table && e.ids.includes(d.id)) || await this._meta().get(QUARANTINE + rowKey2(d.table, d.id)) !== void 0;
      if (queued) continue;
      await this.table(d.table).delete(d.id);
      gone.push({ table: d.table, id: d.id });
    }
  }
  const forget = o.repull;
  if (forget === "all" || forget.length > 0) {
    const caughtUp = await this.getCaughtUpGroups();
    await this.writeCaughtUpGroups(
      forget === "all" ? [] : caughtUp.filter((g) => !forget.includes(g))
    );
  }
  if (o.resetCursor) {
    const was = await this.getLastPulledAt();
    if (was > 0) await this.markRepullSince(was);
    await this.setLastPulledAt(0);
  }
  return () => {
    evictQuarantinedCreates.call(this, o.quarantine);
    for (const { table: table2, id } of gone) this._pendingCache.delete(table2, id);
    for (const t of new Set(gone.map((g) => g.table))) this._emitter.emitBulk(t);
  };
}
async function dropped() {
  const rows = await this._meta().where("key").startsWith(DROPPED).toArray();
  return rows.map((r) => r.value).sort((a, b) => a.at - b.at);
}
async function recordDropped(records) {
  if (records.length === 0) return;
  await this._meta().bulkPut(
    records.map((d) => ({ key: DROPPED + rowKey2(d.table, d.id), value: d }))
  );
}
async function markDroppedRowGone(table2, id) {
  const key = DROPPED + rowKey2(table2, id);
  const prior = (await this._meta().get(key))?.value;
  if (prior === void 0 || prior.columns.includes("__row")) return;
  await this._meta().put({
    key,
    value: { ...prior, columns: [...prior.columns, "__row"], at: Date.now() }
  });
}
async function rehomeQuarantined(table2, id, to) {
  const key = QUARANTINE + rowKey2(table2, id);
  const updated = await this._store.transaction(
    "rw",
    [this.table(table2), this._ops(), this._creates(), this._meta()],
    async () => {
      const held = await this._meta().get(key);
      if (held === void 0) throw new Error(`rehomeQuarantined: nothing quarantined for ${key}`);
      const entry = readEntry(key, held.value);
      if (entry.pendingCreate === void 0)
        throw new Error(`rehomeQuarantined: ${key} holds no unacknowledged create`);
      const column = this.scopeCol(table2);
      if (column === void 0) throw new Error(`rehomeQuarantined: ${table2} has no scope column`);
      const current = await this.table(table2).get(id);
      if (current === void 0)
        throw new Error(`rehomeQuarantined: ${table2}/${id} is no longer stored`);
      const moved = { ...current, [column]: to };
      clearWritten(moved, [column]);
      await this.table(table2).put(moved);
      await putBackInOutbox.call(this, [entry]);
      await this._meta().delete(key);
      return moved;
    }
  );
  if (this._pendingCache.has(table2, id)) this._pendingCache.set(table2, id, updated);
  this._emitter.emitBulk(table2);
}

// src/epoch-rebase.ts
import { COUNTER_SHIFT, formatHlc, MILLIS_SHIFT } from "@jbroll/rowboat-shared";
var SUBMILLIS_MASK = (1n << MILLIS_SHIFT) - 1n;
function buildEpochRebaser(hexes, deltaMs) {
  const clamped = /* @__PURE__ */ new Set();
  let minPostEpochMc = null;
  for (const hex of hexes) {
    const packed = BigInt(`0x${hex}`);
    const rel = packed >> MILLIS_SHIFT;
    if (rel > deltaMs) {
      const mc = (rel - deltaMs << MILLIS_SHIFT | packed & SUBMILLIS_MASK) >> COUNTER_SHIFT;
      if (minPostEpochMc === null || mc < minPostEpochMc) minPostEpochMc = mc;
    } else {
      clamped.add(packed);
    }
  }
  const rankByPacked = /* @__PURE__ */ new Map();
  const ordered = [...clamped].sort((a, b) => a < b ? -1 : a > b ? 1 : 0);
  for (let i = 0; i < ordered.length; i++) rankByPacked.set(ordered[i], BigInt(i));
  if (minPostEpochMc !== null && BigInt(rankByPacked.size - 1) >= minPostEpochMc)
    throw new Error(
      `epoch rebase: ${rankByPacked.size} clamped stamps cannot preserve order below the post-epoch band (${minPostEpochMc} free ordinate slots)`
    );
  return (hex) => {
    const packed = BigInt(`0x${hex}`);
    const rel = packed >> MILLIS_SHIFT;
    if (rel > deltaMs)
      return formatHlc(rel - deltaMs << MILLIS_SHIFT | packed & SUBMILLIS_MASK);
    const rank = rankByPacked.get(packed);
    if (rank === void 0)
      throw new Error(`epoch rebase: clamped stamp ${hex} missing from the ranking set`);
    return formatHlc(rank << COUNTER_SHIFT);
  };
}

// src/db-meta.ts
var OBSERVED_HLC = "observed_hlc";
var REBASED_ECHO_HLC = "rebased_echo_hlc";
function getInstanceId() {
  if (!this._instanceIdPromise) this._instanceIdPromise = this._loadInstanceId();
  return this._instanceIdPromise;
}
async function syncLockName() {
  const instanceId = await this.getInstanceId();
  const dot = instanceId.indexOf(".");
  if (dot <= 0) throw new Error(`instance id ${instanceId} has no persisted part`);
  return `rowboat-sync:${instanceId.slice(0, dot)}`;
}
var EPOCH_KEYS = ["serverEpoch", "localEpoch", "nodeId"];
function parseMark(value) {
  if (typeof value !== "string")
    throw new Error(`${OBSERVED_HLC} ${JSON.stringify(value)} is not an HLC string`);
  return BigInt(`0x${value}`);
}
function parseStoredClock(rows) {
  const num = (i) => {
    const key = EPOCH_KEYS[i];
    const row = rows[i];
    if (row === void 0) throw new Error(`_rowboat_meta has no ${key} record`);
    if (typeof row.value !== "number")
      throw new Error(`_rowboat_meta ${key} ${JSON.stringify(row.value)} is not a number`);
    return row.value;
  };
  const epoch = { serverEpoch: num(0), localEpoch: num(1), nodeId: num(2) };
  const mark = rows[EPOCH_KEYS.length];
  if (mark === void 0) throw new Error(`_rowboat_meta has no ${OBSERVED_HLC} record`);
  return [epoch, parseMark(mark.value)];
}
async function getServerEpoch() {
  await this._ensureOpen();
  return this._raiseToSharedClock();
}
var UNSTAMPED_KEYS = /* @__PURE__ */ new Set([
  "serverEpoch",
  "localEpoch",
  "nodeId",
  "instanceId",
  "lastPulledAt",
  "repull_since",
  "caught_up_groups",
  "group_lost_at",
  "adopting"
]);
var UNSTAMPED_PREFIXES = [DROPPED];
function requireStamp(v, what) {
  if (typeof v !== "string") throw new Error(`epoch rebase: ${what} is not an HLC string`);
  return v;
}
function opStamps(op) {
  const where = `op ${op.table}/${op.id}`;
  const out = [requireStamp(op.hlc, `${where} hlc`)];
  const clocks = JSON.parse(op.changes).__clocks;
  if (clocks === void 0) return out;
  if (clocks === null || typeof clocks !== "object")
    throw new Error(`epoch rebase: ${where} __clocks is not an object`);
  for (const [k, v] of Object.entries(clocks)) out.push(requireStamp(v, `${where} __clocks.${k}`));
  return out;
}
function rebaseOp(op, rebase) {
  const changes = JSON.parse(op.changes);
  const clocks = changes.__clocks;
  if (clocks === void 0) return { ...op, hlc: rebase(op.hlc) };
  const moved = {};
  for (const [k, v] of Object.entries(clocks)) moved[k] = rebase(v);
  changes.__clocks = moved;
  return { ...op, hlc: rebase(op.hlc), changes: JSON.stringify(changes) };
}
async function rebaseStoredStamps(deltaMs) {
  const hexes = [];
  const rowsByTable = /* @__PURE__ */ new Map();
  for (const t of this._tables) {
    const rows = (await this.table(t).toArray()).filter((r) => r.__localHlc !== void 0);
    for (const r of rows) hexes.push(requireStamp(r.__localHlc, `${t}/${r.id} __localHlc`));
    rowsByTable.set(t, rows);
  }
  const ops = await this._ops().toArray();
  for (const op of ops) hexes.push(...opStamps(op));
  const creates = await this._creates().toArray();
  for (const c of creates) hexes.push(requireStamp(c.stamp, `create entry ${c.createId}`));
  const marks = [];
  const parked = [];
  for (const m of await this._meta().toArray()) {
    if (m.key === OBSERVED_HLC || m.key === REBASED_ECHO_HLC) {
      marks.push({ key: m.key, value: requireStamp(m.value, m.key) });
      hexes.push(m.value);
    } else if (m.key.startsWith(QUARANTINE)) {
      const e = readEntry(m.key, m.value);
      for (const op of e.ops) hexes.push(...opStamps(op));
      if (e.pendingCreate !== void 0)
        hexes.push(requireStamp(e.pendingCreate.stamp, `${m.key} pendingCreate`));
      parked.push({ key: m.key, value: e });
    } else if (!UNSTAMPED_KEYS.has(m.key) && !UNSTAMPED_PREFIXES.some((p) => m.key.startsWith(p))) {
      throw new Error(`epoch rebase: _rowboat_meta key "${m.key}" is not a known record`);
    }
  }
  hexes.push(this.__hlc);
  const rebase = buildEpochRebaser(hexes, deltaMs);
  for (const [t, rows] of rowsByTable) {
    for (const r of rows) r.__localHlc = rebase(r.__localHlc);
    if (rows.length > 0) await this.table(t).bulkPut(rows);
  }
  if (ops.length > 0) await this._ops().bulkPut(ops.map((op) => rebaseOp(op, rebase)));
  if (creates.length > 0)
    await this._creates().bulkPut(creates.map((c) => ({ ...c, stamp: rebase(c.stamp) })));
  await this._meta().bulkPut([
    ...marks.map((m) => ({ key: m.key, value: rebase(m.value) })),
    ...parked.map(({ key, value: e }) => ({
      key,
      value: {
        ...e,
        ops: e.ops.map((op) => rebaseOp(op, rebase)),
        ...e.pendingCreate && {
          pendingCreate: { ...e.pendingCreate, stamp: rebase(e.pendingCreate.stamp) }
        }
      }
    }))
  ]);
  return rebase(this.__hlc);
}
async function reconcileEpoch(epoch, roundLocalEpoch) {
  await this._ensureOpen();
  const scope = [
    ...this._tables.map((t) => this.table(t)),
    this._ops(),
    this._creates(),
    this._meta()
  ];
  const cursor = await this._store.transaction("rw", scope, async () => {
    const { localEpoch: from } = await this._raiseToSharedClock();
    if (from !== roundLocalEpoch)
      throw new Error(
        `epoch reconcile: the round ran on local epoch ${roundLocalEpoch} but the store holds ${from}`
      );
    const deltaMs = BigInt(epoch.localEpoch) - BigInt(from);
    const rebased = deltaMs === 0n ? void 0 : await rebaseStoredStamps.call(this, deltaMs);
    await this._meta().bulkPut([
      { key: "serverEpoch", value: epoch.serverEpoch },
      { key: "localEpoch", value: epoch.localEpoch },
      { key: "nodeId", value: epoch.nodeId }
    ]);
    return rebased;
  });
  this._epochMs = epoch.localEpoch;
  if (cursor === void 0) return;
  this.__hlc = cursor;
  this.__hlcVal = BigInt(`0x${cursor}`);
  this._pendingCache.clear();
}
async function getRebasedEchoHlc() {
  const row = await this._meta().get(REBASED_ECHO_HLC);
  if (row === void 0) return void 0;
  if (typeof row.value !== "string") throw new Error(`${REBASED_ECHO_HLC} is not an HLC string`);
  return row.value;
}
async function raiseRebasedEchoHlc(stamp) {
  const cur = await this.getRebasedEchoHlc();
  if (cur === void 0 || stamp > cur)
    await this._meta().put({ key: REBASED_ECHO_HLC, value: stamp });
}
async function getLastPulledAt() {
  const row = await this._meta().get("lastPulledAt");
  return row?.value ?? 0;
}
async function setLastPulledAt(ts) {
  await this._meta().put({ key: "lastPulledAt", value: ts });
}
async function getCaughtUpGroups() {
  const row = await this._meta().get("caught_up_groups");
  return row?.value ?? [];
}
var LOST_AT = "group_lost_at";
async function storedLostAt() {
  const row = await this._meta().get(LOST_AT);
  if (row === void 0) throw new Error(`_rowboat_meta has no ${LOST_AT} record`);
  return row.value;
}
async function getLostAt() {
  await this._ensureOpen();
  return storedLostAt.call(this);
}
async function writeGroups(caughtUp, behind) {
  const before = await this.getCaughtUpGroups();
  const lost = await storedLostAt.call(this);
  const at = await this.getLastPulledAt();
  let lostChanged = false;
  for (const g of [...before, ...behind])
    if (!caughtUp.includes(g) && !(g in lost)) {
      lost[g] = at;
      lostChanged = true;
    }
  for (const g of caughtUp)
    if (g in lost) {
      delete lost[g];
      lostChanged = true;
    }
  const puts = [
    ...lostChanged ? [{ key: LOST_AT, value: lost }] : [],
    ...caughtUp.length !== before.length || caughtUp.some((g, i) => g !== before[i]) ? [{ key: "caught_up_groups", value: caughtUp }] : []
  ];
  if (puts.length > 0) await this._meta().bulkPut(puts);
}
async function setCaughtUpGroups(groups) {
  await this._ensureOpen();
  await this._store.transaction("rw", [this._meta()], () => writeGroups.call(this, groups, []));
}
function writeCaughtUpGroups(groups) {
  return writeGroups.call(this, groups, []);
}
async function advancePullCursor(ts, groups, drained) {
  await this._ensureOpen();
  await this._store.transaction("rw", [this._meta()], async () => {
    const caughtUp = await this.getCaughtUpGroups();
    if (groups === void 0) {
      if (caughtUp.length > 0) throw new Error("pull response missing groups");
    } else {
      const kept = caughtUp.filter((g) => groups.includes(g));
      for (const g of drained) if (!kept.includes(g)) kept.push(g);
      await writeGroups.call(this, kept, groups);
    }
    await this.setLastPulledAt(ts);
  });
}
var isPairs = (v) => Array.isArray(v) && v.every(
  (p) => Array.isArray(p) && p.length === 2 && typeof p[0] === "string" && typeof p[1] === "string"
);
function parseAdoptMarker(value) {
  const fields = typeof value === "object" && value !== null ? Object.entries(value) : [];
  const { anonName, startedAt, scopeGroups } = Object.fromEntries(fields);
  if (typeof anonName !== "string" || typeof startedAt !== "number")
    throw new Error(`adopt: unrecognized marker ${JSON.stringify(value)}`);
  const marker = { anonName, startedAt };
  if (scopeGroups === void 0) return marker;
  if (scopeGroups === null || isPairs(scopeGroups)) return { ...marker, scopeGroups };
  const partial = typeof scopeGroups === "object" ? Object.entries(scopeGroups) : [];
  if (partial.length === 1 && partial[0][0] === "minted" && isPairs(partial[0][1]))
    return { ...marker, scopeGroups: { minted: partial[0][1] } };
  throw new Error(
    `adopt: the marker for ${anonName} started at ${startedAt} has an unrecognized scopeGroups ${JSON.stringify(scopeGroups)}`
  );
}
async function getAdopting() {
  const row = await this._meta().get("adopting");
  return row === void 0 ? null : parseAdoptMarker(row.value);
}
async function setAdopting(v) {
  await this._meta().put({ key: "adopting", value: v });
}
async function clearAdopting() {
  await this._meta().delete("adopting");
}

// src/db-pull.ts
import {
  COUNTER_SHIFT as COUNTER_SHIFT2,
  computeCopyTarget,
  formatHlc as formatHlc2,
  hlcNext,
  isHlcHex
} from "@jbroll/rowboat-shared";

// src/pull-replay.ts
import {
  add,
  decodeOpJson,
  getPath,
  getPathEntry as getPathEntry2,
  hexCompare as hexCompare2,
  inc,
  remove as remove2
} from "@jbroll/rowboat-shared";
function opKey(op) {
  if (op.opId === void 0) throw new Error("pending op read back without its auto-increment key");
  return op.opId;
}
function callChangesOf(op, json, counters, sets) {
  const where = `pending op ${op.table}/${op.id}`;
  const wire = JSON.parse(op.changes);
  const out = {};
  const pathCols = /* @__PURE__ */ new Set();
  const clocks = wire.__clocks;
  if (clocks !== void 0) {
    if (clocks === null || typeof clocks !== "object") throw new Error(`${where}: bad __clocks`);
    for (const key of Object.keys(clocks)) {
      const dot = key.indexOf(".");
      const col = key.slice(0, dot);
      const blob = wire[col];
      if (dot === -1 || blob === null || typeof blob !== "object")
        throw new Error(`${where}: path clock ${key} has no partial blob`);
      const obj = blob;
      const path = key.slice(dot + 1);
      const entry = getPathEntry2(obj, path);
      const parent = path.includes(".") ? getPath(obj, path.slice(0, path.lastIndexOf("."))) : obj;
      if (!entry.found && (parent === null || typeof parent !== "object"))
        throw new Error(`${where}: path ${key} has no parent in its blob`);
      out[key] = entry.value;
      pathCols.add(col);
    }
  }
  for (const [col, v] of Object.entries(wire)) {
    if (col === "__clocks" || pathCols.has(col)) continue;
    if (counters.has(col)) {
      if (typeof v !== "number") throw new Error(`${where}: counter ${col} delta is not a number`);
      out[col] = inc(v);
    } else if (sets.has(col)) {
      const s = v;
      if (s.add !== void 0 && s.remove === void 0) out[col] = add(...s.add);
      else if (s.remove !== void 0 && s.add === void 0) out[col] = remove2(...s.remove);
      else throw new Error(`${where}: set ${col} must carry exactly one of add/remove`);
    } else if (json.has(col) && v !== null) {
      try {
        out[col] = decodeOpJson(v);
      } catch (e) {
        if (!(e instanceof SyntaxError)) throw e;
        throw new Error(`${where}: json column ${col} is not JSON text`);
      }
    } else {
      out[col] = v;
    }
  }
  return out;
}
async function pendingWork(table2, ids, scan) {
  const byId = /* @__PURE__ */ new Map();
  const keep = (op) => {
    const list = byId.get(op.id);
    if (list) list.push(op);
    else byId.set(op.id, [op]);
  };
  if (scan.ops) {
    for (const op of await this._ops().where("id").anyOf(ids).toArray())
      if (op.table === table2) keep(op);
  }
  if (scan.quarantine) {
    for (const ops of (await quarantinedOpsFor.call(this, table2, ids)).values())
      for (const op of ops) keep(op);
  }
  for (const ops of byId.values())
    ops.sort((x, y) => hexCompare2(x.hlc, y.hlc) || opKey(x) - opKey(y));
  return byId;
}
function replayOps(table2, row, ops) {
  const json = this._jsonCols.get(table2) ?? /* @__PURE__ */ new Set();
  const counters = this._counterCols.get(table2) ?? /* @__PURE__ */ new Set();
  const sets = this._setCols.get(table2) ?? /* @__PURE__ */ new Set();
  let cur = row;
  for (const op of ops) {
    if (op.isDelete === 1) {
      cur = { ...cur, __deleted: 1 };
      continue;
    }
    cur = applyChanges(
      table2,
      json,
      counters,
      sets,
      cur,
      callChangesOf(op, json, counters, sets),
      op.hlc,
      this._effectiveCols
    ).updatedRow;
  }
  return cur;
}

// src/db-pull.ts
async function applyLocalCopy(step) {
  const jsonSet = this._jsonCols.get(step.table) ?? /* @__PURE__ */ new Set();
  const isJson = (c) => jsonSet.has(c);
  const t = this.table(step.table);
  const rows = await t.toArray();
  const updates = [];
  for (const row of rows) {
    const unwritten = unwrittenOf(row);
    const newCol = computeCopyTarget(row, step, isJson);
    const unchanged = newCol === row[step.target.col];
    if (unchanged && step.sources.every((s) => unwritten.includes(s.col))) continue;
    const marked = unwritten.includes(step.target.col);
    if (!unchanged || marked) {
      row[step.target.col] = newCol;
      clearWritten(row, [step.target.col]);
      updates.push(row);
    }
  }
  if (updates.length > 0) {
    await t.bulkPut(updates);
    for (const row of updates) this._pendingCache.delete(step.table, row.id);
    this._emitter.emitBulk(step.table);
  }
}
async function heldCreates(table2, local, settled) {
  const want = /* @__PURE__ */ new Set();
  for (const r of local) {
    if (r === void 0 || typeof r.__localHlc !== "string") continue;
    if (!settled?.createRows.has(rowKey2(table2, r.id))) want.add(r.id);
  }
  const held = /* @__PURE__ */ new Set();
  if (want.size === 0) return held;
  for (const e of await this._creates().toArray())
    if (e.table === table2) {
      for (const id of e.ids) if (want.has(id)) held.add(id);
    }
  for (const id of await quarantinedCreatesFor.call(this, table2, [...want])) held.add(id);
  return held;
}
var lwwOrder = (hex) => BigInt(`0x${hex}`) >> COUNTER_SHIFT2;
function applyPull(changes) {
  return applyChangeset.call(this, changes, void 0);
}
function applyEcho(changes, settled) {
  return applyChangeset.call(this, changes, settled);
}
function assertKnownTables(tableNames, who) {
  const knownTables = new Set(this._tables);
  for (const t of tableNames)
    if (!knownTables.has(t))
      throw new Error(
        `${who}: the reply names table ${t}, which this store does not hold \u2014 protocol error`
      );
}
function partition(changes, who) {
  const parts = /* @__PURE__ */ new Map();
  let maxObserved = 0n;
  for (const [t, { upserted }] of Object.entries(changes)) {
    const live = [];
    const deleted = [];
    const clocks = {};
    for (const r of upserted) {
      const rec = r;
      const ts = rec.__row_ts;
      if (rec.__op !== void 0 && rec.__op !== "remove")
        throw new Error(`${who}: ${t} record has unknown __op \u2014 protocol error`);
      if (!isHlcHex(ts))
        throw new Error(
          `${who}: ${t}/${String(rec.id)} has no valid __row_ts (${JSON.stringify(ts)}) \u2014 protocol error`
        );
      const v = BigInt(`0x${ts}`);
      if (v > maxObserved) maxObserved = v;
      if (rec.__op === void 0) {
        live.push(rec);
        continue;
      }
      const id = rec.id;
      if (typeof id !== "string")
        throw new Error(`${who}: remove record for ${t} lacks an id \u2014 protocol error`);
      deleted.push(id);
      clocks[id] = ts;
    }
    parts.set(t, { live, deleted, clocks });
  }
  return { parts, maxObserved };
}
async function advanceClock(maxObserved) {
  await this._raiseToSharedClock();
  const relMillis = BigInt(Math.max(0, this._nowFn() - this._epochMs));
  const advanced = hlcNext(this.__hlcVal, maxObserved, relMillis);
  this.__hlcVal = advanced;
  this.__hlc = formatHlc2(advanced);
  await this._publishClock();
}
async function applyLive(t, live, scan, settled, out) {
  const ids = live.map((r) => r.id);
  const pending = live.length > 0 && (scan.ops || scan.quarantine) ? await pendingWork.call(this, t, ids, scan) : void 0;
  const localRows = live.length > 0 && (scan.creates || scan.quarantine) ? await this.table(t).bulkGet(ids) : void 0;
  const held = localRows === void 0 ? void 0 : await heldCreates.call(this, t, localRows, settled);
  const upserts = [];
  for (let i = 0; i < live.length; i++) {
    const r = live[i];
    const local = localRows?.[i];
    if (local !== void 0 && local.__deleted === 0 && held?.has(local.id)) {
      const stamp = local.__localHlc;
      if (stamp > out.echoMark) out.echoMark = stamp;
      continue;
    }
    const row = { ...r, __deleted: 0 };
    delete row.__row_ts;
    const ops = pending?.get(r.id);
    if (ops === void 0) {
      upserts.push(row);
      continue;
    }
    upserts.push(replayOps.call(this, t, row, ops));
    const rowTs = r.__row_ts;
    for (const op of ops) {
      if (op.isDelete === 1 || lwwOrder(op.hlc) > lwwOrder(rowTs)) continue;
      if (op.hlc > out.echoMark) out.echoMark = op.hlc;
    }
  }
  if (upserts.length) {
    await this.table(t).bulkPut(upserts);
    for (const u of upserts) out.evicted.push([t, u.id]);
    out.affected.add(t);
  }
}
async function applyTombstones(t, deleted, clocks, scan, settled, out) {
  const idSet = new Set(deleted);
  const localRows = await this.table(t).bulkGet(deleted);
  const heldHere = scan.creates || scan.quarantine ? await heldCreates.call(this, t, localRows, settled) : /* @__PURE__ */ new Set();
  const tableOps = (await this._ops().toArray()).filter((o) => o.table === t && idSet.has(o.id));
  const maxDeleteOpHlc = /* @__PURE__ */ new Map();
  for (const o of tableOps) {
    if (o.isDelete !== 1) continue;
    const prev = maxDeleteOpHlc.get(o.id);
    if (prev === void 0 || o.hlc > prev) maxDeleteOpHlc.set(o.id, o.hlc);
  }
  const toDelete = deleted.filter((id, i) => {
    const tomb = clocks[id];
    const localHlc = localRows[i]?.__localHlc;
    const delOpHlc = maxDeleteOpHlc.get(id);
    let newest = typeof localHlc === "string" && heldHere.has(id) ? localHlc : "";
    if (delOpHlc !== void 0 && delOpHlc > newest) newest = delOpHlc;
    return !(newest > tomb);
  });
  if (toDelete.length) {
    await this.table(t).bulkDelete(toDelete);
    for (const id of toDelete) out.evicted.push([t, id]);
    const delSet = new Set(toDelete);
    const orphanOpIds = tableOps.filter((o) => {
      if (!delSet.has(o.id) || o.opId === void 0) return false;
      if (o.isDelete !== 1) return true;
      return !(o.hlc > clocks[o.id]);
    }).map((o) => o.opId);
    if (orphanOpIds.length > 0) await this._ops().bulkDelete(orphanOpIds);
  }
  out.affected.add(t);
}
async function applyChangeset(changes, settled) {
  const who = settled === void 0 ? "applyPull" : "applyEcho";
  const tableNames = Object.keys(changes);
  assertKnownTables.call(this, tableNames, who);
  if (tableNames.length === 0 && settled === void 0) return;
  const settledOps = settled === void 0 ? [] : [...settled.opIds];
  const { parts, maxObserved } = partition(changes, who);
  await this._ensureOpen();
  const writes = /* @__PURE__ */ new Set([...tableNames, ...settled ? refusedTables(settled.refused) : []]);
  const scope = [
    ...[...writes].map((t) => this._store.table(t)),
    this._ops(),
    this._creates(),
    this._meta()
  ];
  const out = { evicted: [], affected: /* @__PURE__ */ new Set(), echoMark: "" };
  let retired = [];
  let afterRefused = () => {
  };
  await this._store.transaction("rw", scope, async () => {
    if (maxObserved > 0n) await advanceClock.call(this, maxObserved);
    if (settledOps.length > 0) await this._ops().bulkDelete(settledOps);
    if (settled !== void 0) {
      retired = await deleteCreateEntries.call(this, settled.createIds);
      afterRefused = await writeRefused.call(this, settled.refused);
    }
    const scan = {
      ops: await this._ops().count() > 0,
      quarantine: await this.quarantinedCount() > 0,
      creates: await this._creates().count() > 0
    };
    for (const [t, { live, deleted, clocks }] of parts) {
      await applyLive.call(this, t, live, scan, settled, out);
      if (deleted.length) await applyTombstones.call(this, t, deleted, clocks, scan, settled, out);
    }
    if (out.echoMark !== "") await this.raiseRebasedEchoHlc(out.echoMark);
  });
  evictRetiredCreates.call(this, retired);
  afterRefused();
  for (const [t, id] of out.evicted) this._pendingCache.delete(t, id);
  for (const t of out.affected) this._emitter.emitBulk(t);
}
async function lostAtOf(groups) {
  const caughtUp = await this.getCaughtUpGroups();
  const lost = await this.getLostAt();
  return groups.flatMap((g) => caughtUp.includes(g) || !(g in lost) ? [] : [lost[g]]);
}
async function wipeAndResetForRepull() {
  const tables = this.registeredTables();
  const wiped = await this._store.transaction(
    "rw",
    [...tables.map((t) => this.table(t)), this._ops(), this._creates(), this._meta()],
    async () => {
      if (await this._ops().count() > 0 || await this._creates().count() > 0 || (await this.quarantined()).some((e) => e.pendingCreate !== void 0))
        return false;
      for (const t of tables) await this.table(t).clear();
      this._pendingCache.clear();
      await this.setLastPulledAt(0);
      await this._meta().bulkPut([
        { key: "caught_up_groups", value: [] },
        { key: LOST_AT, value: {} }
      ]);
      return true;
    }
  );
  if (wiped) for (const t of tables) this._emitter.emitBulk(t);
  return wiped;
}

// src/sync-lock.ts
function pickSyncLock(g, caller) {
  const locks = g.navigator?.locks;
  if (locks !== void 0) return { kind: "web", locks };
  if (g.process?.versions?.node !== void 0) return { kind: "realm" };
  throw new Error(`${caller}: this runtime has no Web Locks API to keep sync rounds apart`);
}
var realmLocks = /* @__PURE__ */ new Map();
async function withSyncLock(name, caller, fn) {
  const lock = pickSyncLock(globalThis, caller);
  if (lock.kind === "web") {
    await lock.locks.request(name, fn);
    return;
  }
  const ahead = realmLocks.get(name);
  let release = () => {
  };
  const mine = new Promise((r) => {
    release = r;
  });
  const tail = ahead === void 0 ? mine : ahead.then(() => mine);
  realmLocks.set(name, tail);
  if (ahead !== void 0) await ahead;
  try {
    await fn();
  } finally {
    release();
    if (realmLocks.get(name) === tail) realmLocks.delete(name);
  }
}

// src/db-quarantine-discard.ts
async function discardQuarantined(table2, id) {
  const key = QUARANTINE + rowKey2(table2, id);
  let removed = false;
  await withSyncLock(await this.syncLockName(), "discardQuarantined", async () => {
    removed = await this._store.transaction(
      "rw",
      [this.table(table2), this._ops(), this._meta()],
      async () => {
        const held = await this._meta().get(key);
        if (held === void 0)
          throw new Error(`discardQuarantined: nothing quarantined for ${key}`);
        const entry = readEntry(key, held.value);
        const created = entry.pendingCreate !== void 0;
        const queued = created ? (await this._ops().where("table").equals(table2).toArray()).filter((o) => o.id === id) : [];
        const columns = created ? ["__created", ...opColumns([...entry.ops, ...queued]).filter((c) => c !== "__created")] : opColumns(entry.ops);
        if (queued.length > 0) await this._ops().bulkDelete(queued.map((o) => o.opId));
        await this._meta().delete(key);
        await recordDropped.call(this, [
          { table: table2, id, reason: entry.reason, columns, at: Date.now() }
        ]);
        const was = await this.getLastPulledAt();
        if (was > 0) await markRepullSince.call(this, was);
        await this.setLastPulledAt(0);
        if (created) await this.table(table2).delete(id);
        return created;
      }
    );
  });
  this._pendingCache.delete(table2, id);
  if (removed) this._emitter.emitBulk(table2);
}
var REPULL_SINCE = "repull_since";
async function getRepullSince() {
  return (await this._meta().get(REPULL_SINCE))?.value;
}
async function markRepullSince(cursor) {
  const held = await getRepullSince.call(this);
  if (held === void 0 || cursor < held)
    await this._meta().put({ key: REPULL_SINCE, value: cursor });
}
async function clearRepullSince() {
  await this._meta().delete(REPULL_SINCE);
}
function opColumns(ops) {
  const out = /* @__PURE__ */ new Set();
  for (const op of ops) {
    if (op.isDelete === 1) out.add("__deleted");
    else
      for (const k of Object.keys(JSON.parse(op.changes)))
        if (k !== "__clocks") out.add(k);
  }
  return [...out];
}

// src/db-subscription.ts
function table(name) {
  return this._store.table(name);
}
function readTables(names) {
  return this._store.transaction(
    "r",
    names,
    () => Promise.all(names.map((n) => this._store.table(n).toArray()))
  );
}
function close() {
  this._emitter.close();
  this._store.close();
}
function registeredTables() {
  return [...this._tables];
}
function counterCols(table2) {
  return this._counterCols.get(table2) ?? /* @__PURE__ */ new Set();
}
function setCols(table2) {
  return this._setCols.get(table2) ?? /* @__PURE__ */ new Set();
}
function scopeCol(table2) {
  return this._scopeCols.get(table2);
}
function subscribe(listener) {
  return this._emitter.subscribe(listener);
}

// src/pending-cache.ts
var PendingRowCache = class {
  _byTable = /* @__PURE__ */ new Map();
  set(table2, id, row) {
    let rows = this._byTable.get(table2);
    if (!rows) {
      rows = /* @__PURE__ */ new Map();
      this._byTable.set(table2, rows);
    }
    rows.set(id, row);
  }
  get(table2, id) {
    return this._byTable.get(table2)?.get(id);
  }
  has(table2, id) {
    return this._byTable.get(table2)?.has(id) ?? false;
  }
  delete(table2, id) {
    this._byTable.get(table2)?.delete(id);
  }
  clear() {
    this._byTable.clear();
  }
  size() {
    let n = 0;
    for (const rows of this._byTable.values()) n += rows.size;
    return n;
  }
};

// src/row-decode.ts
function decodeRow(table2, row) {
  if (!this._effectiveCols.has(table2))
    throw new Error(`decodeRow: ${table2} has no known columns at this store's version`);
  const json = this._jsonCols.get(table2);
  const sets = this._setCols.get(table2);
  const bools = this._boolCols.get(table2);
  if (json === void 0 && sets === void 0 && bools === void 0) return row;
  const out = { ...row };
  const fail = (col, why) => new Error(`decodeRow: ${table2}/${row.id} column ${col} ${why}`);
  const parse = (col) => {
    const v = out[col];
    if (typeof v !== "string") throw fail(col, `holds a ${typeof v}, not its stored text`);
    try {
      return JSON.parse(v);
    } catch {
      throw fail(col, "holds text that is not JSON");
    }
  };
  for (const col of json ?? []) {
    if (!(col in out) || out[col] === null) continue;
    if (out[col] === "") delete out[col];
    else out[col] = parse(col);
  }
  for (const col of sets ?? []) {
    if (!(col in out) || out[col] === null) continue;
    const v = parse(col);
    if (!Array.isArray(v)) throw fail(col, "does not hold an array");
    out[col] = v;
  }
  for (const col of bools ?? []) {
    if (!(col in out) || out[col] === null) continue;
    if (out[col] !== 0 && out[col] !== 1) throw fail(col, "holds a boolean other than 0/1");
    out[col] = out[col] === 1;
  }
  return out;
}

// src/db.ts
var RowboatDb = class {
  _store;
  _emitter;
  _tables;
  __hlc;
  __hlcVal;
  // The local epoch this instance stamps on: the build argument until _open adopts the stored one.
  _epochMs;
  _jsonCols;
  _counterCols;
  _setCols;
  _boolCols;
  _effectiveCols;
  // Table -> column -> what the server stores for it when no write names it (column-defaults.ts).
  _colDefaults;
  _scopeCols;
  _storeTables;
  _tablesAboveCap;
  _nowFn;
  _openPromise;
  // Per-tab clock-node component (in-memory: distinct per tab, stable for this tab's lifetime so a
  // push and its follow-up pull agree on the node). Combined with the persisted per-browser
  // instanceId in getInstanceId().
  _tabNonce = globalThis.crypto.randomUUID();
  _pendingCache = new PendingRowCache();
  _instanceIdPromise;
  constructor(store, tables, emitter, hlc, epochMs, jsonCols, counterCols2, setCols2, boolCols, nowFn, effectiveCols, colDefaults, scopeCols, storeTables, tablesAboveCap) {
    this._store = store;
    this._tables = tables;
    this._emitter = emitter;
    this.__hlc = hlc;
    this.__hlcVal = BigInt(`0x${hlc}`);
    this._epochMs = epochMs;
    this._jsonCols = jsonCols;
    this._counterCols = counterCols2;
    this._setCols = setCols2;
    this._boolCols = boolCols;
    this._nowFn = nowFn;
    this._effectiveCols = effectiveCols;
    this._colDefaults = colDefaults;
    this._scopeCols = scopeCols;
    this._storeTables = storeTables;
    this._tablesAboveCap = tablesAboveCap;
  }
  // The columns that exist at this store's CAPPED version — the graph routes movedFrom writes off
  // this, and create/update reject a column outside it. NOT the app's full manifest.
  effectiveColumns() {
    return this._effectiveCols;
  }
  // Manifest (current) table name -> the store table that holds it at this store's CAPPED version,
  // which is a pre-rename name below a pending renameTable. A table added above the cap is absent.
  storeTables() {
    return this._storeTables;
  }
  // Manifest tables a migration above the cap adds -> the schema version that adds them.
  tablesAboveCap() {
    return this._tablesAboveCap;
  }
  // Runs once per instance before anything reads the epoch or the clock mark, which every read
  // after it requires (R231). A failed open is dropped so the next call runs it again: a missing
  // record fails the same way every time, and a transient store error gets its retry.
  _ensureOpen() {
    if (!this._openPromise) {
      const opening = this._open();
      this._openPromise = opening;
      opening.catch(() => {
        if (this._openPromise === opening) this._openPromise = void 0;
      });
    }
    return this._openPromise;
  }
  // A store with no epoch records has never been opened: it starts on the epoch this instance was
  // built with. The clock mark is raised to every stamp already stored (C2): a pending op, create or
  // quarantined write minted before the mark was published must not sit above it.
  async _open() {
    const scope = [this._meta(), this._ops(), this._creates()];
    await this._store.transaction("rw", scope, async () => {
      const stored = await this._meta().bulkGet([...EPOCH_KEYS, OBSERVED_HLC]);
      const epochRows = stored.slice(0, EPOCH_KEYS.length);
      const fresh = epochRows.every((r) => r === void 0);
      if (fresh)
        await this._meta().bulkPut([
          { key: "serverEpoch", value: 0 },
          { key: "localEpoch", value: this._epochMs },
          { key: "nodeId", value: 0 },
          // A new store has lost no group.
          { key: LOST_AT, value: {} }
        ]);
      const markRow = stored[EPOCH_KEYS.length];
      let max = markRow === void 0 ? 0n : parseMark(markRow.value);
      const consider = (hex) => {
        const v = BigInt(`0x${hex}`);
        if (v > max) max = v;
      };
      for (const op of await this._ops().toArray()) consider(op.hlc);
      for (const c of await this._creates().toArray()) consider(c.stamp);
      for (const e of await this.quarantined()) {
        for (const op of e.ops) consider(op.hlc);
        if (e.pendingCreate !== void 0) consider(e.pendingCreate.stamp);
      }
      if (markRow === void 0 || max > parseMark(markRow.value))
        await this._meta().put({ key: OBSERVED_HLC, value: formatHlc3(max) });
      await this._raiseToSharedClock();
    });
  }
  // Tabs of one profile share the store but each keeps its own clock, so a stamp minted or observed
  // in another tab reaches this one only through the persisted mark (R52), and an epoch another tab
  // reconciled reaches it through the stored localEpoch (R231). Runs inside the caller's rw
  // transaction, which must cover _rowboat_meta: read before stamping, publish after.
  async _raiseToSharedClock() {
    const [epoch, mark] = parseStoredClock(
      await this._meta().bulkGet([...EPOCH_KEYS, OBSERVED_HLC])
    );
    if (epoch.localEpoch !== this._epochMs) {
      this._epochMs = epoch.localEpoch;
      this.__hlcVal = mark;
      this.__hlc = formatHlc3(mark);
      this._pendingCache.clear();
    } else if (mark > this.__hlcVal) {
      this.__hlcVal = mark;
      this.__hlc = formatHlc3(mark);
    }
    return epoch;
  }
  async _publishClock() {
    await this._meta().put({ key: OBSERVED_HLC, value: this.__hlc });
  }
  _nextHlc() {
    const relMillis = BigInt(Math.max(0, this._nowFn() - this._epochMs));
    const val = hlcNext2(this.__hlcVal, 0n, relMillis);
    this.__hlcVal = val;
    this.__hlc = formatHlc3(val);
    return this.__hlc;
  }
  _ops() {
    return this._store.table("_rowboat_ops");
  }
  _creates() {
    return this._store.table("_rowboat_creates");
  }
  _meta() {
    return this._store.table("_rowboat_meta");
  }
  async _loadInstanceId() {
    const meta = this._meta();
    const installId = await this._store.transaction("rw", [meta], async () => {
      const existing = await meta.get("instanceId");
      const found = existing?.value;
      if (found) return found;
      const minted = globalThis.crypto.randomUUID();
      await meta.put({ key: "instanceId", value: minted });
      return minted;
    });
    return `${installId}.${this._tabNonce}`;
  }
  // delegated methods
  create = create;
  update = update;
  remove = remove;
  pendingOps = pendingOps;
  hardDeleteAcked = hardDeleteAcked;
  clearOps = clearOps;
  pendingCacheSize = pendingCacheSize;
  pendingCreateEntries = pendingCreateEntries;
  pendingCreates = pendingCreates;
  unwrittenAtDefault = unwrittenAtDefault2;
  clearCreates = clearCreates;
  splitCreateEntries = splitCreateEntries;
  applyPull = applyPull;
  applyEcho = applyEcho;
  wipeAndResetForRepull = wipeAndResetForRepull;
  applyLocalCopy = applyLocalCopy;
  getInstanceId = getInstanceId;
  syncLockName = syncLockName;
  getServerEpoch = getServerEpoch;
  reconcileEpoch = reconcileEpoch;
  getLastPulledAt = getLastPulledAt;
  setLastPulledAt = setLastPulledAt;
  advancePullCursor = advancePullCursor;
  getCaughtUpGroups = getCaughtUpGroups;
  setCaughtUpGroups = setCaughtUpGroups;
  writeCaughtUpGroups = writeCaughtUpGroups;
  getLostAt = getLostAt;
  getRebasedEchoHlc = getRebasedEchoHlc;
  raiseRebasedEchoHlc = raiseRebasedEchoHlc;
  // Writes the server refused, set aside so a refused push can't starve the pull.
  quarantined = quarantined;
  quarantinedCount = quarantinedCount;
  retryQuarantined = retryQuarantined;
  discardQuarantined = discardQuarantined;
  getRepullSince = getRepullSince;
  markRepullSince = markRepullSince;
  clearRepullSince = clearRepullSince;
  requeueQuarantined = requeueQuarantined;
  rehomeQuarantined = rehomeQuarantined;
  // Writes the client discarded because they could never be accepted — see DroppedRecord.
  dropped = dropped;
  recordDropped = recordDropped;
  markDroppedRowGone = markDroppedRowGone;
  getAdopting = getAdopting;
  setAdopting = setAdopting;
  clearAdopting = clearAdopting;
  table = table;
  // The read contract: a stored row decoded to its declared types (row-decode.ts).
  decodeRow = decodeRow;
  readTables = readTables;
  close = close;
  registeredTables = registeredTables;
  counterCols = counterCols;
  setCols = setCols;
  scopeCol = scopeCol;
  subscribe = subscribe;
};

// src/db-migration.ts
import {
  CastRefusedError,
  chainStepOverRow,
  copySourcesCarried,
  resolveCastFn,
  TransformOutputTooLargeError
} from "@jbroll/rowboat-shared";
function resolveMigrationCopyFns(migrations) {
  return migrations.map((m) => ({
    ...m,
    steps: m.steps.map((step) => {
      if (step.type !== "copyFromExpression" || step.fn) return step;
      if (step.cast) return { ...step, fn: resolveCastFn(step.cast, step.sources[0].col) };
      throw new Error(
        `rowboat: author transform for ${step.table}.${step.target.col} needs a live function on the client \u2014 provide it as \`fn\` on the migration step (the browser cannot evaluate a source string)`
      );
    })
  }));
}
function tableIndexString(columns) {
  const indexed = columns.filter((c) => c.isIndexed).map((c) => c.name);
  return ["id", ...indexed].join(", ");
}
var NOT_A_COPY = {
  isJson: () => false,
  column: (col) => {
    throw new Error(`rowboat: only a copy step looks up a column (${col})`);
  }
};
function perStepColumnsForChain(start, sorted) {
  const state = cloneChain(start.state);
  const byVersion2 = /* @__PURE__ */ new Map();
  for (const m of sorted) {
    const perStep = m.steps.map((step) => {
      let cols = NOT_A_COPY;
      if (step.type === "copyFromExpression") {
        const t = state.get(step.table);
        if (t === void 0)
          throw new Error(`rowboat: copyFromExpression targets unknown table '${step.table}'`);
        const recorded = new Map(t.cols);
        const column = (col) => {
          const c = recorded.get(col);
          if (c !== void 0) return c;
          throw new Error(
            `rowboat: a copy reads ${step.table}.${col}, which the chain does not hold`
          );
        };
        cols = { column, isJson: (col) => column(col).type === "json" };
      }
      applyStep(state, step, start.creates);
      return cols;
    });
    byVersion2.set(m.toVersion, perStep);
  }
  return byVersion2;
}
var domainValues = (row) => {
  const unwritten = new Set(unwrittenOf(row));
  return Object.fromEntries(
    Object.entries(row).filter(([k]) => k !== "id" && !k.startsWith("__") && !unwritten.has(k))
  );
};
async function discardDestroyedTable(tx, table2, store, fromVersion, toVersion, onDrop) {
  const report = { reason: "destroyTable", table: table2, fromVersion, toVersion };
  const reportOp = (op) => {
    const changes = JSON.parse(op.changes);
    delete changes.__clocks;
    const kind = op.isDelete === 1 ? "delete" : "update";
    onDrop({ ...report, id: op.id, hlc: op.hlc, op: kind, changes });
  };
  const reportCreate = (r, stamp) => {
    if (r === void 0 || r.__deleted !== 0 || typeof r.__localHlc !== "string") return;
    onDrop({ ...report, id: r.id, hlc: stamp, op: "create", changes: domainValues(r) });
  };
  const opsTable = tx.table("_rowboat_ops");
  const ops = await opsTable.where("table").equals(table2).toArray();
  for (const op of ops) reportOp(op);
  await opsTable.where("table").equals(table2).delete();
  const createsTable = tx.table("_rowboat_creates");
  const entries = (await createsTable.toArray()).filter(
    (e) => e.table === table2
  );
  for (const e of entries) {
    if (e.createId === void 0)
      throw new Error("discardDestroyedTable: side-log entry has no key");
    const rows = await tx.table(store).bulkGet(e.ids);
    for (const r of rows) reportCreate(r, e.stamp);
    await createsTable.delete(e.createId);
  }
  const metaTable = tx.table("_rowboat_meta");
  for (const prefix of [QUARANTINE, DROPPED]) {
    const keyed = metaTable.where("key").startsWith(prefix + rowKey2(table2, ""));
    if (prefix === QUARANTINE) {
      const parked = await keyed.toArray();
      for (const { key, value } of parked) {
        const e = readEntry(key, value);
        for (const op of e.ops) reportOp(op);
        if (e.pendingCreate) reportCreate(await tx.table(store).get(e.id), e.pendingCreate.stamp);
      }
    }
    await keyed.delete();
  }
}
var isCopyRefusal = (e) => e instanceof CastRefusedError || e instanceof TransformOutputTooLargeError;
function refused(e, step, cols) {
  const toType = () => step.cast === void 0 ? cols.column(step.target.col).type : step.cast.targetType;
  if (e instanceof CastRefusedError) {
    if (e.column === void 0)
      throw new Error("rowboat: a copy step's cast refusal names no column");
    return { column: e.column, value: e.value, toType: toType() };
  }
  if (e.value === void 0)
    throw new Error("rowboat: a transform size refusal on the client carries no value");
  const [src] = step.sources;
  const column = src.path ? `${src.col}.${src.path}` : src.col;
  return { column, value: e.value, toType: toType() };
}
function castRefusedDrop(step, e, cols, table2, id, hlc, fromVersion, toVersion) {
  const { column, value, toType } = refused(e, step, cols);
  return {
    reason: "castRefused",
    table: table2,
    id,
    column,
    value,
    toType,
    hlc,
    fromVersion,
    toVersion
  };
}
function stepTable(s) {
  return "table" in s ? s.table : void 0;
}
function reframeOpsForMigration(m, fromVersion, stepCols, onDrop) {
  const reframe = (op) => {
    const values = JSON.parse(op.changes);
    const clocks = {};
    const consumed = /* @__PURE__ */ new Set();
    let table2 = op.table;
    for (let i = m.steps.length - 1; i >= 0; i--) {
      const s = m.steps[i];
      if (s.type === "renameTable" && s.to === table2) table2 = s.from;
    }
    for (const [i, step] of m.steps.entries()) {
      if (step.type === "renameTable" && step.from === table2) table2 = step.to;
      if (table2 !== stepTable(step)) continue;
      if (step.type === "copyFromExpression" && copySourcesCarried(step, values)) {
        for (const src of step.sources) consumed.add(src.col);
      }
      if (step.type === "dropColumn" && step.column in values && !consumed.has(step.column)) {
        onDrop({
          reason: "dropColumn",
          table: op.table,
          id: op.id,
          column: step.column,
          value: values[step.column],
          hlc: op.hlc,
          fromVersion,
          toVersion: m.toVersion
        });
      }
      try {
        chainStepOverRow(step, values, clocks, op.hlc, stepCols[i].isJson);
      } catch (e) {
        if (step.type !== "copyFromExpression" || !isCopyRefusal(e)) throw e;
        onDrop(
          castRefusedDrop(step, e, stepCols[i], op.table, op.id, op.hlc, fromVersion, m.toVersion)
        );
      }
    }
    return values;
  };
  const emptied = (op, values) => Object.keys(values).length === 0 && op.isDelete === 0;
  return async (tx) => {
    const ops = await tx.table("_rowboat_ops").toArray();
    for (const op of ops) {
      const values = reframe(op);
      if (emptied(op, values)) await tx.table("_rowboat_ops").delete(op.opId);
      else await tx.table("_rowboat_ops").update(op.opId, { changes: JSON.stringify(values) });
    }
    const meta = tx.table("_rowboat_meta");
    const parked = await meta.where("key").startsWith(QUARANTINE).toArray();
    for (const { key, value } of parked) {
      const entry = readEntry(key, value);
      const kept = [];
      for (const op of entry.ops) {
        const values = reframe(op);
        if (!emptied(op, values)) kept.push({ ...op, changes: JSON.stringify(values) });
      }
      if (kept.length === 0 && entry.pendingCreate === void 0) await meta.delete(key);
      else await meta.put({ key, value: { ...entry, ops: kept } });
    }
  };
}

// src/dexie-store.ts
import { Dexie } from "dexie";
var defaultDexieStoreFactory = (name, opts) => new Dexie(name, opts);

// src/events.ts
var ChangeEmitter = class {
  _listeners = /* @__PURE__ */ new Set();
  _transport;
  setTransport(transport) {
    this._transport = transport;
  }
  emit(event) {
    this._dispatch(event);
    this._transport?.broadcast(event.table);
  }
  emitBulk(table2) {
    this._dispatch({ table: table2, type: "bulk" });
    this._transport?.broadcast(table2);
  }
  // Inbound cross-tab seam: a peer tab's committed change reaches us as table names only. Dispatch a
  // local bulk (drives a requery of the SHARED IndexedDB) and do NOT re-broadcast — that is what stops
  // a cross-tab echo loop.
  applyRemote(tables) {
    for (const table2 of tables) this._dispatch({ table: table2, type: "bulk" });
  }
  // Isolate each subscriber: a throwing listener (an app-side handler bug) must not stop delivery to
  // the others or unwind the caller (a sync-kick subscriber could otherwise be skipped). Surface the
  // error to the console rather than swallowing it silently.
  _dispatch(event) {
    for (const l of this._listeners) {
      try {
        l(event);
      } catch (err) {
        console.error("rowboat change listener threw", err);
      }
    }
  }
  subscribe(listener) {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }
  close() {
    this._transport?.close();
  }
};

// src/db-factory.ts
function resetToDefault(row, table2, col) {
  const [[, defaults]] = defaultsByTable(/* @__PURE__ */ new Map([[table2, /* @__PURE__ */ new Map([[col.name, col]])]]));
  delete row[col.name];
  clearUnwritten(row, col.name);
  fillDefaults(row, defaults);
}
function buildRowboatDb(name, tables, migrationsIn, epochMs, options) {
  const migrations = resolveMigrationCopyFns(migrationsIn);
  const storeFactory = options?.storeFactory ?? defaultDexieStoreFactory;
  const dexie = storeFactory(name, options);
  const emitter = new ChangeEmitter();
  const transport = createCrossTabChannel(
    name,
    (tables2) => emitter.applyRemote(tables2),
    options?.crossTab !== false
  );
  if (transport) emitter.setTransport(transport);
  const chain = unwindChain(tables, migrations);
  const v1Stores = {
    _rowboat_meta: "key",
    _rowboat_ops: "++opId, table, id",
    _rowboat_creates: "++createId"
  };
  const currentIndexes = {};
  for (const [t, start] of chain.state) {
    const s = tableIndexString([...start.cols.values()]);
    v1Stores[t] = s;
    currentIndexes[t] = s;
  }
  dexie.version(1).stores(v1Stores);
  const maxVersion = options?.maxVersion ?? Number.POSITIVE_INFINITY;
  const sorted = [...migrations].sort((a, b) => a.toVersion - b.toVersion).filter((m) => m.toVersion <= maxVersion);
  const chainCols = perStepColumnsForChain(chain, sorted);
  const onDrop = options?.onReframeDrop ?? ((d) => {
    const what = d.reason === "dropColumn" ? `a pending edit: ${d.table}.${d.column}` : d.reason === "castRefused" ? `a pending value ${d.table}.${d.column} that a ${d.toType} cast refused` : `a pending ${d.op} of destroyed table ${d.table}`;
    console.warn(
      `[rowboat] op-log migration dropped ${what} (id=${d.id}) across v${d.fromVersion}\u2192v${d.toVersion}`
    );
  });
  for (const m of sorted) {
    const delta = {};
    const hopRenames = [];
    const storeOf = (table2, renames) => {
      let n = table2;
      for (let i = renames.length - 1; i >= 0; i--)
        if (renames[i].to === n && delta[n] === null) n = renames[i].from;
      return n;
    };
    const upgraders = [];
    let openAdd;
    const setIdx = (table2, s) => {
      delta[table2] = s;
      currentIndexes[table2] = s;
    };
    const indexOf = (table2, what) => {
      const s = currentIndexes[table2];
      if (s === void 0)
        throw new Error(
          `buildRowboatDb: migration v${m.toVersion} ${what} targets unknown table '${table2}' \u2014 never declared by this chain, or destroyed earlier in it`
        );
      return s;
    };
    const tokens = (table2) => indexOf(table2, "column step").split(", ");
    const stepCols = chainCols.get(m.toVersion);
    if (!stepCols) throw new Error(`reframe: no per-step columns computed for v${m.toVersion}`);
    for (const [stepIndex, step] of m.steps.entries()) {
      if (step.type === "addTable") {
        if (delta[step.def.name] === null)
          throw new Error(
            `buildRowboatDb: migration v${m.toVersion} adds table '${step.def.name}' after destroying or renaming away a table of that name in the same version; put the addTable in a later version`
          );
        setIdx(step.def.name, tableIndexString(step.def.columns));
      } else if (step.type === "addColumn") {
        if (step.column.isIndexed)
          setIdx(step.table, [...tokens(step.table), step.column.name].join(", "));
        if (openAdd?.table === step.table && openAdd.at === upgraders.length) {
          openAdd.columns.push(step.column);
        } else {
          const columns = [step.column];
          openAdd = { table: step.table, at: upgraders.length + 1, columns };
          upgraders.push(
            (tx) => tx.table(step.table).toCollection().modify((row) => {
              for (const column of columns) addColumnDefault(row, column);
            })
          );
        }
      } else if (step.type === "destroyTable") {
        delta[step.name] = null;
        delete currentIndexes[step.name];
        const renamesBefore = [...hopRenames];
        upgraders.push(
          (tx) => discardDestroyedTable(
            tx,
            step.name,
            storeOf(step.name, renamesBefore),
            m.toVersion - 1,
            m.toVersion,
            onDrop
          )
        );
      } else if (step.type === "renameColumn") {
        const toks = tokens(step.table);
        if (toks.includes(step.from))
          setIdx(step.table, toks.map((t) => t === step.from ? step.to : t).join(", "));
        upgraders.push(
          (tx) => tx.table(step.table).toCollection().modify((row) => {
            if (step.from in row) {
              row[step.to] = row[step.from];
              delete row[step.from];
            }
            renameUnwritten(row, step.from, step.to);
          })
        );
      } else if (step.type === "dropColumn") {
        const toks = tokens(step.table);
        if (toks.includes(step.column))
          setIdx(step.table, toks.filter((t) => t !== step.column).join(", "));
        upgraders.push(
          (tx) => tx.table(step.table).toCollection().modify((row) => {
            delete row[step.column];
            clearUnwritten(row, step.column);
          })
        );
      } else if (step.type === "copyFromExpression") {
        const s = step;
        const cols = stepCols[stepIndex];
        const { isJson, column } = cols;
        let reportTable = s.table;
        for (const later of m.steps.slice(stepIndex + 1))
          if (later.type === "renameTable" && later.from === reportTable) reportTable = later.to;
        upgraders.push(
          (tx) => tx.table(s.table).toCollection().modify((row) => {
            if (typeof row.__localHlc !== "string") return;
            const unwritten = new Set(unwrittenOf(row));
            const nothingToCopy = s.sources.length > 0 && s.sources.every((src) => unwritten.has(src.col)) && (s.cast !== void 0 || s.sources.every((src) => isJson(src.col) && (src.path ?? "") === ""));
            if (nothingToCopy) return;
            try {
              row[s.target.col] = computeCopyTarget2(row, s, isJson);
              clearUnwritten(row, s.target.col);
            } catch (e) {
              if (!isCopyRefusal(e)) throw e;
              if ((s.target.path ?? "") === "") {
                if (s.cast && s.cast.targetType !== "json") {
                  row[s.target.col] = conversionFn(s.cast.targetType, s.cast.optional)(null);
                  clearUnwritten(row, s.target.col);
                } else {
                  resetToDefault(row, s.table, column(s.target.col));
                }
              }
              const id = row.id;
              const hlc2 = row.__localHlc;
              onDrop(
                castRefusedDrop(s, e, cols, reportTable, id, hlc2, m.toVersion - 1, m.toVersion)
              );
            }
          })
        );
      } else if (step.type === "addIndex") {
        const toks = tokens(step.table);
        if (!toks.includes(step.column)) setIdx(step.table, [...toks, step.column].join(", "));
      } else if (step.type === "dropIndex") {
        const toks = tokens(step.table);
        if (toks.includes(step.column))
          setIdx(step.table, toks.filter((t) => t !== step.column).join(", "));
      } else if (step.type === "renameTable") {
        setIdx(step.to, indexOf(step.from, "renameTable"));
        delta[step.from] = null;
        delete currentIndexes[step.from];
        const renamesBefore = [...hopRenames];
        hopRenames.push(step);
        upgraders.push(async (tx) => {
          if (delta[step.to] !== null) {
            const rows = await tx.table(storeOf(step.from, renamesBefore)).toArray();
            if (rows.length > 0) await tx.table(step.to).bulkPut(rows);
          }
          await tx.table("_rowboat_ops").where("table").equals(step.from).modify({
            table: step.to
          });
          await tx.table("_rowboat_creates").toCollection().modify((entry) => {
            if (entry.table === step.from) entry.table = step.to;
          });
          await renameMetaRecords(tx.table("_rowboat_meta"), step.from, step.to);
        });
      } else if (step.type !== "setColumnMeta") {
        throw new Error(
          `buildRowboatDb: migration v${m.toVersion} has unrecognized step type ${step.type}`
        );
      }
    }
    upgraders.push(reframeOpsForMigration(m, m.toVersion - 1, stepCols, onDrop));
    const hasDelta = Object.keys(delta).length > 0;
    if (hasDelta || upgraders.length > 0) {
      const version = dexie.version(m.toVersion).stores(hasDelta ? delta : {});
      if (upgraders.length > 0)
        version.upgrade(async (tx) => {
          for (const u of upgraders) await u(tx);
        });
    }
  }
  const tableNames = Object.keys(currentIndexes);
  const capped = chainAtCap(chain, migrations, maxVersion);
  const { storeTables, tablesAboveCap } = capped;
  for (const [table2, n] of storeTables)
    if (currentIndexes[n] === void 0)
      throw new Error(`buildRowboatDb: manifest table '${table2}' resolves to no store ('${n}')`);
  const epochMsVal = epochMs ?? DEFAULT_EPOCH_MS;
  const hlc = hexPack(0, 0);
  const jsonCols = columnsOfType(capped.columns, "json");
  const counterCols2 = columnsOfType(capped.columns, "counter");
  const setCols2 = columnsOfType(capped.columns, "set");
  const boolCols = columnsOfType(capped.columns, "boolean");
  const effectiveCols = /* @__PURE__ */ new Map();
  for (const [t, cols] of capped.columns) effectiveCols.set(t, new Set(cols.keys()));
  const scopeCols = /* @__PURE__ */ new Map();
  for (const [table2, cols] of capped.columns) {
    const scoped = [...cols.values()].filter((c) => c.scope);
    if (scoped.length > 1)
      throw new Error(
        `buildRowboatDb: table "${table2}" declares ${scoped.length} scope columns (${scoped.map((c) => c.name).join(", ")}) \u2014 ambiguous scoping`
      );
    if (scoped.length === 1) scopeCols.set(table2, scoped[0].name);
  }
  return new RowboatDb(
    dexie,
    tableNames,
    emitter,
    hlc,
    epochMsVal,
    jsonCols,
    counterCols2,
    setCols2,
    boolCols,
    options?.nowFn ?? Date.now,
    effectiveCols,
    defaultsByTable(capped.columns),
    scopeCols,
    storeTables,
    tablesAboveCap
  );
}

// src/adopt.ts
var ANON_IDENTITY = "anon";
function storeName(app, identity) {
  return `${app}::${identity}`;
}
function assertAdoptable(source) {
  for (const t of source.registeredTables()) {
    if (source.counterCols(t).size > 0 || source.setCols(t).size > 0)
      throw new Error(`adopt: table ${t} has counter/set columns (unsupported in adopt v1)`);
  }
}
async function adoptRows(dest, source, opts) {
  const { scopeColumn } = opts;
  const tables = source.registeredTables();
  assertAdoptable(source);
  const groupOf = (t, r) => {
    if ("scopeGroupId" in opts) return opts.scopeGroupId;
    const scope = r[scopeColumn];
    const group = typeof scope === "string" ? opts.scopeGroups.get(scope) : void 0;
    if (group === void 0)
      throw new Error(`adopt: no target group for ${t} row ${r.id} (scope ${String(scope)})`);
    return group;
  };
  const targetId = (r, group) => r.id === r[scopeColumn] ? group : r.id;
  const sourceRows = await source.readTables(tables);
  const live = sourceRows.map((rows) => rows.filter((r) => r.__deleted !== 1));
  const groups = live.map((rows, ti) => rows.map((r) => groupOf(tables[ti], r)));
  for (const [ti, t] of tables.entries()) {
    const liveRows = live[ti];
    const tids = liveRows.map((r, i) => targetId(r, groups[ti][i]));
    const present = await dest.table(t).bulkGet(tids);
    const seen = /* @__PURE__ */ new Set();
    const toCreate = [];
    for (let i = 0; i < liveRows.length; i++) {
      const tid = tids[i];
      if (present[i] !== void 0 || seen.has(tid)) continue;
      seen.add(tid);
      const data = { ...source.decodeRow(t, liveRows[i]) };
      for (const col of source.unwrittenAtDefault(t, liveRows[i])) delete data[col];
      delete data.__localHlc;
      delete data.__deleted;
      delete data[UNWRITTEN_FIELD];
      toCreate.push({ ...data, id: tid, [scopeColumn]: groups[ti][i] });
    }
    if (toCreate.length > 0) await dest.create(t, toCreate);
  }
}
var DELETE_BLOCKED_TIMEOUT_MS = 5e3;
function deleteDexieDb(name, options) {
  const idb = options.indexedDB ?? globalThis.indexedDB;
  return new Promise((resolve, reject) => {
    let timer;
    const req = idb.deleteDatabase(name);
    req.onsuccess = () => {
      clearTimeout(timer);
      resolve();
    };
    req.onerror = () => {
      clearTimeout(timer);
      reject(req.error);
    };
    req.onblocked = () => {
      if (timer !== void 0) return;
      timer = setTimeout(
        () => reject(
          new Error(
            `deleteDexieDb: delete of ${name} still blocked by an open connection after ${DELETE_BLOCKED_TIMEOUT_MS}ms`
          )
        ),
        DELETE_BLOCKED_TIMEOUT_MS
      );
    };
  });
}
async function liveScopes(source, scopeColumn) {
  const tables = source.registeredTables();
  const scopes = /* @__PURE__ */ new Set();
  for (const [ti, rows] of (await source.readTables(tables)).entries()) {
    for (const r of rows) {
      if (r.__deleted === 1) continue;
      const scope = r[scopeColumn];
      if (typeof scope !== "string")
        throw new Error(`adopt: ${tables[ti]} row ${r.id} has no ${scopeColumn}`);
      scopes.add(scope);
    }
  }
  return scopes;
}
var markerLabel = (m) => `the marker for ${m.anonName} started at ${m.startedAt}`;
async function adoptTarget(acct, anon, marker, args) {
  const recorded = marker.scopeGroups;
  if (recorded === void 0) return { scopeGroupId: args.scopeGroupId };
  const complete = Array.isArray(recorded);
  const { mintScope } = args;
  if (!complete && mintScope === void 0)
    throw new Error(
      `adopt: ${markerLabel(marker)} is minting a group per anonymous scope, so resuming it needs mintScope`
    );
  assertAdoptable(anon);
  const scopes = await liveScopes(anon, args.scopeColumn);
  const groups = new Map(complete ? recorded : recorded === null ? [] : recorded.minted);
  for (const scope of scopes) {
    if (scope === ANON_IDENTITY || groups.has(scope)) continue;
    if (mintScope === void 0)
      throw new Error(
        `adopt: ${markerLabel(marker)} has no group for anonymous scope ${scope}, and minting one needs mintScope`
      );
    groups.set(scope, await mintScope(scope, `${marker.startedAt}:${scope}`));
    const pairs = [...groups];
    await acct.setAdopting({ ...marker, scopeGroups: complete ? pairs : { minted: pairs } });
  }
  if (scopes.has(ANON_IDENTITY) && !groups.has(ANON_IDENTITY))
    groups.set(ANON_IDENTITY, args.scopeGroupId);
  await acct.setAdopting({ ...marker, scopeGroups: [...groups] });
  return { scopeGroups: groups };
}
async function runAdopt(acct, marker, args) {
  const locked = storeName(args.app, ANON_IDENTITY);
  if (marker.anonName !== locked)
    throw new Error(
      `adopt: ${markerLabel(marker)} names a store other than ${locked}, the one the claim locked`
    );
  const anon = buildRowboatDb(
    marker.anonName,
    args.tables,
    args.migrations ?? [],
    void 0,
    args.options
  );
  try {
    const target = await adoptTarget(acct, anon, marker, args);
    await adoptRows(acct, anon, { scopeColumn: args.scopeColumn, ...target });
  } finally {
    anon.close();
  }
  await deleteDexieDb(marker.anonName, args.options);
  await acct.clearAdopting();
}
var ANON_LOCK_TIMEOUT_MS = 1e4;
var AnonStoreBusyError = class extends Error {
  constructor(store) {
    super(
      `adopt: the anonymous store ${store} stayed open in another tab or page for ${ANON_LOCK_TIMEOUT_MS}ms, so the claim did not start`
    );
    this.store = store;
    this.name = "AnonStoreBusyError";
  }
  store;
};
function webLocks() {
  const locks = typeof navigator === "undefined" ? void 0 : navigator.locks;
  if (locks === void 0 || locks === null)
    throw new Error(
      "adopt: navigator.locks (the Web Locks API) is required to keep two tabs from claiming at once"
    );
  return locks;
}
async function underClaimLocks(args, claim) {
  const locks = webLocks();
  const name = storeName(args.app, args.accountId);
  const anonName = storeName(args.app, ANON_IDENTITY);
  const cancel = new AbortController();
  let granted = false;
  let bound;
  const checkHolder = () => {
    locks.query().then(
      (snapshot) => {
        if (granted) return;
        if (snapshot.held === void 0) {
          cancel.abort(new Error(`adopt: navigator.locks.query() returned no held list`));
          return;
        }
        const claimHolds = snapshot.held.some((l) => l.name === anonName && l.mode === "exclusive");
        if (claimHolds) bound = setTimeout(checkHolder, ANON_LOCK_TIMEOUT_MS);
        else cancel.abort(new AnonStoreBusyError(anonName));
      },
      (e) => cancel.abort(e)
    );
  };
  bound = setTimeout(checkHolder, ANON_LOCK_TIMEOUT_MS);
  const locked = locks.request(anonName, { mode: "exclusive", signal: cancel.signal }, () => {
    granted = true;
    clearTimeout(bound);
    return locks.request(`adopt:${name}`, { mode: "exclusive" }, async () => {
      const acct = buildRowboatDb(
        name,
        args.tables,
        args.migrations ?? [],
        void 0,
        args.options
      );
      try {
        await claim(acct);
      } finally {
        acct.close();
      }
    });
  });
  try {
    args.closeAnon?.();
  } catch (e) {
    cancel.abort(e);
  }
  try {
    await locked;
  } finally {
    clearTimeout(bound);
  }
}
function adoptAnonStore(args) {
  return underClaimLocks(args, async (acct) => {
    const interrupted = await acct.getAdopting();
    if (interrupted !== null) {
      await runAdopt(acct, interrupted, args);
      return;
    }
    const marker = {
      anonName: storeName(args.app, ANON_IDENTITY),
      startedAt: (args.now ?? Date.now)()
    };
    if (args.mintScope !== void 0) marker.scopeGroups = null;
    await acct.setAdopting(marker);
    await runAdopt(acct, marker, args);
  });
}
function resumeAdopt(args) {
  return underClaimLocks(args, async (acct) => {
    const marker = await acct.getAdopting();
    if (marker !== null) await runAdopt(acct, marker, args);
  });
}

// src/blob-queue.ts
async function toBlob(input) {
  if (input instanceof Blob) return input;
  if (input instanceof ArrayBuffer) return new Blob([input]);
  return new Blob([
    input.buffer.slice(input.byteOffset, input.byteOffset + input.byteLength)
  ]);
}
function memoryQueueStore() {
  const m = /* @__PURE__ */ new Map();
  return {
    put: async (k, b) => {
      m.set(k, b);
    },
    get: async (k) => m.get(k),
    delete: async (k) => {
      m.delete(k);
    },
    list: async () => [...m.keys()]
  };
}
function indexedDbQueueStore(dbName = "rowboat-blob-uploads", storeName2 = "pending", deps) {
  const idb = deps?.indexedDB ?? globalThis.indexedDB;
  const open = () => new Promise((resolve, reject) => {
    const req = idb.open(dbName, 1);
    req.onupgradeneeded = () => {
      req.result.createObjectStore(storeName2);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  const run = (mode, fn) => open().then(
    (db) => new Promise((resolve, reject) => {
      const tx = db.transaction(storeName2, mode);
      const req = fn(tx.objectStore(storeName2));
      tx.oncomplete = () => {
        db.close();
        resolve(req.result);
      };
      tx.onabort = () => {
        db.close();
        reject(
          new Error(`blob queue: ${mode} transaction on ${dbName}/${storeName2} aborted`, {
            cause: tx.error
          })
        );
      };
    })
  );
  return {
    put: (k, b) => b.arrayBuffer().then((buf) => run("readwrite", (s) => s.put({ type: b.type, buf }, k))).then(() => void 0),
    get: (k) => run("readonly", (s) => s.get(k)).then((v) => {
      if (v === void 0) return void 0;
      if (v instanceof Blob) return v;
      const s = v;
      if (typeof s?.type === "string" && s?.buf instanceof ArrayBuffer)
        return new Blob([s.buf], { type: s.type });
      throw new Error(`blob queue entry ${k} holds non-Blob value`);
    }),
    delete: (k) => run("readwrite", (s) => s.delete(k)).then(() => void 0),
    list: () => run("readonly", (s) => s.getAllKeys()).then((ks) => ks.map(String))
  };
}
function createBlobUploadQueue(opts) {
  const { client, store, commit, derivatives = [], onStatus, ownerGroupId } = opts;
  if (typeof ownerGroupId !== "function")
    throw new Error("createBlobUploadQueue: ownerGroupId is required (key => the row's group)");
  return {
    enqueue: (key, bytes) => toBlob(bytes).then((b) => store.put(key, b)),
    pending: () => store.list().then((l) => l.length),
    discard: async (key) => {
      if (!(await store.list()).includes(key))
        throw new Error(`blob queue: no entry queued under ${key}`);
      await store.delete(key);
    },
    drain: async () => {
      let uploaded = 0;
      const failed = [];
      for (const key of await store.list()) {
        const bytes = await store.get(key).catch((e) => {
          throw new Error(
            `blob queue: entry ${key} cannot be read back (remove it with discard("${key}")): ${e instanceof Error ? e.message : String(e)}`,
            { cause: e }
          );
        });
        if (bytes === void 0) {
          await store.delete(key);
          continue;
        }
        try {
          const group = await ownerGroupId(key);
          if (typeof group !== "string" || group === "")
            throw new Error(`blob queue: ownerGroupId(${key}) named no group`);
          const primary = await client.upload(bytes, { ownerGroupId: group });
          const derResults = {};
          for (const d of derivatives) {
            try {
              derResults[d.name] = await client.upload(await d.make(bytes), {
                ownerGroupId: group
              });
            } catch (e) {
              console.warn(`blob queue derivative ${d.name} failed for ${key}:`, e);
            }
          }
          await commit(key, { primary, derivatives: derResults });
          await store.delete(key);
          uploaded++;
        } catch (e) {
          failed.push({ key, error: e instanceof Error ? e : new Error(String(e)) });
        }
      }
      if (onStatus) onStatus({ pending: await store.list().then((l) => l.length), failed });
      return { uploaded, failed };
    }
  };
}

// src/blob-hash.ts
async function sha256Hex(bytes) {
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
}

// src/files.ts
async function toArrayBuffer(blob) {
  if (blob instanceof ArrayBuffer) return blob;
  if (blob instanceof Uint8Array)
    return blob.buffer.slice(blob.byteOffset, blob.byteOffset + blob.byteLength);
  return blob.arrayBuffer();
}
function createBlobClient(options) {
  const { apiBase, author, headers, fetchFn = fetch, nowFn = Date.now } = options;
  const skew = options.refreshSkewMs ?? 5e3;
  const cacheSize = options.signedUrlCacheSize ?? 1e3;
  if (!Number.isInteger(cacheSize) || cacheSize < 1)
    throw new Error(`signedUrlCacheSize must be a positive integer, got ${cacheSize}`);
  const cache = /* @__PURE__ */ new Map();
  const baseHeaders = async () => {
    const base = {
      ...headers,
      ...author ? { "x-author": author } : {}
    };
    if (base.Authorization !== void 0) return base;
    if (options.authProvider)
      base.Authorization = `Bearer ${await options.authProvider.getToken()}`;
    return base;
  };
  async function getSigned(hash) {
    const cached = cache.get(hash);
    if (cached && nowFn() < cached.exp - skew) {
      cache.delete(hash);
      cache.set(hash, cached);
      return cached;
    }
    const res = await fetchFn(`${apiBase}/${hash}/sign`, {
      method: "POST",
      headers: await baseHeaders()
    });
    if (!res.ok) throw new Error(`sign failed: ${res.status}`);
    const body = await res.json();
    if (typeof body.url !== "string" || typeof body.exp !== "number" || !Number.isFinite(body.exp) || typeof body.token !== "string")
      throw new Error(`sign response malformed: ${JSON.stringify(body)}`);
    const url = new URL(body.url, res.url).href;
    const signed = { url, exp: body.exp, token: body.token };
    cache.delete(hash);
    cache.set(hash, signed);
    if (cache.size > cacheSize) cache.delete(cache.keys().next().value);
    return signed;
  }
  async function verifiedFetch(res, hash) {
    if (!res.ok) return res;
    const bytes = await res.arrayBuffer();
    const got = await sha256Hex(bytes);
    if (got !== hash) throw new Error(`blob hash mismatch: got ${got} != requested ${hash}`);
    return new Response(bytes, { status: res.status, headers: res.headers });
  }
  return {
    async upload(blob, opts) {
      const ownerGroupId = opts?.ownerGroupId;
      if (typeof ownerGroupId !== "string" || ownerGroupId === "")
        throw new Error(
          "upload: ownerGroupId is required (the group of the row the bytes are for)"
        );
      const bytes = await toArrayBuffer(blob);
      const res = await fetchFn(`${apiBase}/upload`, {
        method: "POST",
        headers: {
          ...await baseHeaders(),
          "content-type": "application/octet-stream",
          "x-owner-group": ownerGroupId
        },
        body: bytes
      });
      if (res.status === 403) {
        const code = readForbidden(await res.text())?.code;
        if (code !== void 0) throw new AccountRefusedError("upload", code);
      }
      if (!res.ok) throw new Error(`upload failed: ${res.status}`);
      const result = await res.json();
      const expected = await sha256Hex(bytes);
      if (result.hash !== expected)
        throw new Error(`upload hash mismatch: server ${result.hash} != local ${expected}`);
      if (result.size !== bytes.byteLength)
        throw new Error(`upload size mismatch: server ${result.size} != local ${bytes.byteLength}`);
      return { hash: result.hash, size: result.size };
    },
    async getUrl(hash) {
      return (await getSigned(hash)).url;
    },
    async fetch(hash) {
      const { url, exp, token } = await getSigned(hash);
      const auth = { Authorization: `Bearer ${exp}.${token}` };
      const res = await fetchFn(url, { headers: auth });
      if (res.status === 403) {
        cache.delete(hash);
        const s2 = await getSigned(hash);
        return verifiedFetch(
          await fetchFn(s2.url, {
            headers: { Authorization: `Bearer ${s2.exp}.${s2.token}` }
          }),
          hash
        );
      }
      return verifiedFetch(res, hash);
    },
    invalidate(hash) {
      cache.delete(hash);
    }
  };
}

// src/frac-key.ts
var DIGITS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
var BASE = DIGITS.length;
var SUFFIX_LEN = 6;
function digitValue(ch) {
  const v = DIGITS.indexOf(ch);
  if (v === -1) throw new Error(`fracKey: invalid digit "${ch}" (not base-62)`);
  return v;
}
function randomInt(min, max) {
  return min + Math.floor(Math.random() * (max - min + 1));
}
function randomNonZeroDigit() {
  return DIGITS[randomInt(1, BASE - 1)];
}
function randomSuffix() {
  let s = "";
  for (let k = 0; k < SUFFIX_LEN; k++) s += randomNonZeroDigit();
  return s;
}
function generate(lower, upper) {
  let result = "";
  let lowFree = false;
  let highFree = false;
  for (let i = 0; ; i++) {
    const lowDigit = lowFree || i >= lower.length ? -1 : digitValue(lower[i]);
    let highDigit;
    if (highFree || upper === void 0) {
      highDigit = BASE;
    } else if (i < upper.length) {
      highDigit = digitValue(upper[i]);
    } else {
      throw new Error(`fracKey: no key exists between adjacent keys "${lower}" and "${upper}"`);
    }
    const freeBothLo = lowDigit === -1 ? 0 : lowDigit + 1;
    const freeBothHi = highDigit === BASE ? BASE - 1 : highDigit - 1;
    if (freeBothLo <= freeBothHi) {
      const d2 = randomInt(freeBothLo, freeBothHi);
      return result + DIGITS[d2] + randomSuffix();
    }
    const d = lowDigit === -1 ? 0 : lowDigit;
    result += DIGITS[d];
    if (lowDigit === -1 || d > lowDigit) lowFree = true;
    if (highDigit === BASE || d < highDigit) highFree = true;
  }
}
var fracKey = {
  first() {
    return DIGITS[Math.floor(BASE / 3)];
  },
  last() {
    return DIGITS[Math.floor(2 * BASE / 3)];
  },
  between(a, b) {
    if (a !== void 0 && b !== void 0 && a >= b) {
      throw new Error(`fracKey.between: lower bound "${a}" must be < upper bound "${b}"`);
    }
    return generate(a ?? "", b);
  }
};

// src/hooks.ts
import { useCallback, useEffect, useRef, useState } from "react";

// src/predicate.ts
function matchesPredicate(record, pred) {
  switch (pred.op) {
    case "eq":
      return record[pred.column] === pred.value;
    case "in":
      return pred.values.includes(record[pred.column]);
    case "gt":
      return record[pred.column] > pred.value;
    case "gte":
      return record[pred.column] >= pred.value;
    case "lt":
      return record[pred.column] < pred.value;
    case "lte":
      return record[pred.column] <= pred.value;
    case "between": {
      const v = record[pred.column];
      return v >= pred.lo && v <= pred.hi;
    }
    case "and":
      return pred.predicates.every((p) => matchesPredicate(record, p));
  }
}
function dexieQuery(tbl, pred) {
  const base = pred ? applyPred(tbl, pred) : tbl.toCollection();
  return base.filter((r) => !r.__deleted);
}
function applyPred(tbl, pred) {
  switch (pred.op) {
    case "eq":
      return tbl.where(pred.column).equals(pred.value);
    case "in":
      return tbl.where(pred.column).anyOf(pred.values);
    case "gt":
      return tbl.where(pred.column).above(pred.value);
    case "gte":
      return tbl.where(pred.column).aboveOrEqual(pred.value);
    case "lt":
      return tbl.where(pred.column).below(pred.value);
    case "lte":
      return tbl.where(pred.column).belowOrEqual(pred.value);
    case "between":
      return tbl.where(pred.column).between(pred.lo, pred.hi, true, true);
    case "and": {
      if (pred.predicates.length === 0) return tbl.toCollection();
      const [first, ...rest] = pred.predicates;
      let coll = applyPred(tbl, first);
      for (const p of rest) coll = coll.filter((r) => matchesPredicate(r, p));
      return coll;
    }
  }
}

// src/hooks.ts
var decoded = (db, table2, row) => db.decodeRow(table2, row);
function useRows(db, table2, pred = null) {
  const [results, setResults] = useState([]);
  const resultIds = useRef(/* @__PURE__ */ new Set());
  const gen = useRef(0);
  const latestRequery = useRef(0);
  const requery = useCallback(async () => {
    for (; ; ) {
      const myGen = ++gen.current;
      latestRequery.current = myGen;
      const stored = await dexieQuery(db.table(table2), pred).toArray();
      const rows = stored.map((r) => decoded(db, table2, r));
      if (myGen === gen.current) {
        resultIds.current = new Set(rows.map((r) => r.id));
        setResults(rows);
        return;
      }
      if (latestRequery.current !== myGen) return;
    }
  }, [db, table2, pred]);
  useEffect(() => {
    void requery();
  }, [requery]);
  useEffect(() => {
    return db.subscribe((event) => {
      if (event.table !== table2) return;
      if (event.type === "bulk") {
        void requery();
        return;
      }
      gen.current++;
      const { type, id, record } = event;
      if (type === "deleted") {
        if (resultIds.current.has(id)) {
          resultIds.current.delete(id);
          setResults((prev) => prev.filter((r) => r.id !== id));
        }
        return;
      }
      const matches = !!record && !record.__deleted && (pred === null || matchesPredicate(record, pred));
      const wasIn = resultIds.current.has(id);
      if (matches && !wasIn) {
        resultIds.current.add(id);
        setResults((prev) => [...prev, decoded(db, table2, record)]);
      } else if (!matches && wasIn) {
        resultIds.current.delete(id);
        setResults((prev) => prev.filter((r) => r.id !== id));
      } else if (matches && wasIn) {
        const row = decoded(db, table2, record);
        setResults((prev) => prev.map((r) => r.id === id ? row : r));
      }
    });
  }, [db, table2, pred, requery]);
  return results;
}
function useRow(db, table2, id) {
  const [record, setRecord] = useState(null);
  const gen = useRef(0);
  const fetchOne = useCallback(async () => {
    const myGen = ++gen.current;
    const row = await db.table(table2).get(id);
    if (myGen !== gen.current) return;
    setRecord(row && !row.__deleted ? decoded(db, table2, row) : null);
  }, [db, table2, id]);
  useEffect(() => {
    void fetchOne();
  }, [fetchOne]);
  useEffect(() => {
    return db.subscribe((event) => {
      if (event.table !== table2) return;
      if (event.type === "bulk") {
        void fetchOne();
        return;
      }
      if (event.id !== id) return;
      gen.current++;
      if (event.type === "deleted") {
        setRecord(null);
      } else {
        const r = event.record;
        setRecord(r && !r.__deleted ? decoded(db, table2, r) : null);
      }
    });
  }, [db, table2, id, fetchOne]);
  return record;
}

// src/media-cell.ts
function mediaCell(r) {
  if (typeof r.hash !== "string" || typeof r.size !== "number" || !Number.isFinite(r.size))
    throw new Error(`invalid UploadResult: ${JSON.stringify(r)}`);
  return { hash: r.hash, size: r.size };
}
function parseMediaCell(v) {
  if (v === null || v === void 0) return void 0;
  if (typeof v !== "object") throw new Error(`media cell is not an object: ${JSON.stringify(v)}`);
  const o = v;
  if (typeof o.hash !== "string")
    throw new Error(`media cell hash is not a string: ${JSON.stringify(v)}`);
  if (typeof o.size !== "number" || !Number.isFinite(o.size))
    throw new Error(`media cell size is not a finite number: ${JSON.stringify(v)}`);
  return { hash: o.hash, size: o.size };
}

// src/offline-blobs.ts
function createOfflineBlobStore(options) {
  const { client } = options;
  const cacheName = options.cacheName ?? "rowboat-blobs";
  const cacheStorage = options.caches ?? globalThis.caches;
  const storage = options.storage ?? globalThis.navigator?.storage;
  const keyFor = options.keyFor ?? ((hash) => `/files/${hash}`);
  const hashFor = options.hashFor ?? ((key) => key.split("/").pop() ?? "");
  const openCache = () => cacheStorage.open(cacheName);
  let persistenceRequested = false;
  async function requestPersistence() {
    if (!storage || persistenceRequested) return;
    persistenceRequested = true;
    const p = storage.persist?.();
    if (p) await p.catch(() => false);
  }
  async function fetchVerified(hash) {
    const res = await client.fetch(hash);
    if (!res.ok) throw new Error(`fetch ${hash}: status ${res.status}`);
    const bytes = await res.arrayBuffer();
    const got = await sha256Hex(bytes);
    if (got !== hash) throw new Error(`fetched bytes for ${hash} do not hash to it (got ${got})`);
    return { bytes, type: res.headers.get("content-type") ?? "application/octet-stream" };
  }
  return {
    async pin(hashes) {
      await requestPersistence();
      const cache = await openCache();
      let pinned = 0;
      let alreadyPinned = 0;
      const failed = [];
      for (const hash of hashes) {
        const key = keyFor(hash);
        if (await cache.match(key)) {
          alreadyPinned++;
          continue;
        }
        let verified;
        try {
          verified = await fetchVerified(hash);
        } catch (err) {
          failed.push({ hash, error: err instanceof Error ? err : new Error(String(err)) });
          continue;
        }
        await cache.put(
          key,
          new Response(verified.bytes, {
            status: 200,
            headers: {
              "content-type": verified.type,
              "content-length": String(verified.bytes.byteLength)
            }
          })
        );
        pinned++;
      }
      return { pinned, alreadyPinned, failed };
    },
    async unpin(hashes) {
      const cache = await openCache();
      for (const hash of hashes) await cache.delete(keyFor(hash));
    },
    async has(hash) {
      const cache = await openCache();
      return await cache.match(keyFor(hash)) !== void 0;
    },
    async get(hash) {
      const cache = await openCache();
      const hit = await cache.match(keyFor(hash));
      return hit ?? client.fetch(hash);
    },
    async list() {
      const cache = await openCache();
      const reqs = await cache.keys();
      return reqs.map((r) => hashFor(new URL(r.url).pathname)).filter((h) => h.length > 0);
    }
  };
}

// src/ordered-list.ts
var RESERVED = /* @__PURE__ */ new Set(["__order", "__deleted"]);
function orderedToArray(map) {
  return Object.values(map ?? {}).filter((el) => el.__deleted !== true).sort((x, y) => {
    const a = x.__order ?? "\uFFFF";
    const b = y.__order ?? "\uFFFF";
    return a < b ? -1 : a > b ? 1 : 0;
  }).map((el) => {
    const out = {};
    for (const [k, v] of Object.entries(el)) if (!RESERVED.has(k)) out[k] = v;
    return out;
  });
}
function orderedList(opts) {
  const { path, key, read, update: update2 } = opts;
  const elementId = (id) => {
    if (id === "") throw new Error(`orderedList: element id in '${path}' is empty`);
    if (id.includes("."))
      throw new Error(`orderedList: element id '${id}' in '${path}' contains '.'`);
    return id;
  };
  const keyOf = (el) => {
    const v = el[key];
    if (typeof v === "string" && v !== "" || typeof v === "number" && Number.isFinite(v))
      return elementId(String(v));
    throw new Error(
      `orderedList.append: element key '${key}' in '${path}' must be a non-empty string or finite number, got ${JSON.stringify(v)}`
    );
  };
  const sortedEntries = () => Object.entries(read() ?? {}).filter(([, el]) => el.__deleted !== true).map(([id, el]) => ({ id, order: el.__order ?? "\uFFFF" })).sort((a, b) => a.order < b.order ? -1 : a.order > b.order ? 1 : 0);
  return {
    toArray: () => orderedToArray(read()),
    append: async (el) => {
      const id = keyOf(el);
      const entries = sortedEntries();
      const lastOrder = entries.length > 0 ? entries[entries.length - 1].order : void 0;
      await update2({
        [`${path}.${id}`]: { ...el, __order: fracKey.between(lastOrder, void 0) }
      });
    },
    setField: async (elId, field, value) => {
      await update2({ [`${path}.${elementId(elId)}.${field}`]: value });
    },
    remove: async (elId) => {
      await update2({ [`${path}.${elementId(elId)}.__deleted`]: true });
    },
    move: async (elId, pos) => {
      elementId(elId);
      const entries = sortedEntries().filter((e) => e.id !== elId);
      const anchorAt = (id) => {
        const i = entries.findIndex((e) => e.id === id);
        if (i < 0)
          throw new Error(
            `orderedList.move: anchor '${id}' is not in '${path}' (moving '${elId}')`
          );
        return i;
      };
      let lower;
      let upper;
      if (pos.after !== void 0) {
        const i = anchorAt(pos.after);
        lower = entries[i].order;
        upper = i + 1 < entries.length ? entries[i + 1].order : void 0;
      } else if (pos.before !== void 0) {
        const i = anchorAt(pos.before);
        upper = entries[i].order;
        lower = i > 0 ? entries[i - 1].order : void 0;
      }
      await update2({ [`${path}.${elId}.__order`]: fracKey.between(lower, upper) });
    }
  };
}

// src/sync-backfill.ts
async function fetchPage(p, body) {
  const res = await p.pullFetch(body);
  if (res.status === 409) {
    const action = await p.readGate(res);
    if (action !== null) return action;
  }
  if (res.status === 503 || res.status === 425) return "residency";
  if (res.status === 403) {
    const code = readForbidden(await res.text())?.code;
    if (code !== void 0) throw new AccountRefusedError("pull", code);
  }
  if (!res.ok) throw new Error(`Pull failed: ${res.status}`);
  const page = await res.json();
  if (page.groups === void 0) throw new Error("pull response missing groups");
  if (page.hasMore === void 0) throw new Error("pull response missing hasMore");
  if (typeof page.maxPurgedCursor !== "number")
    throw new Error("pull response missing maxPurgedCursor");
  return {
    maxPurgedCursor: page.maxPurgedCursor,
    changes: page.changes,
    timestamp: page.timestamp,
    hasMore: page.hasMore,
    groups: page.groups
  };
}
async function pullGroup(p, group, lostAt) {
  let cursor = 0;
  for (; ; ) {
    const page = await fetchPage(p, { lastPulledAt: cursor, groups: [group] });
    if (typeof page === "string") return page;
    if (!page.groups.includes(group)) return "revoked";
    if (lostAt !== void 0 && page.maxPurgedCursor > lostAt) await p.db.markRepullSince(lostAt);
    await p.db.applyPull(page.changes);
    if (!page.hasMore) return void 0;
    cursor = page.timestamp;
  }
}
async function runGroupBackfill(params) {
  const { db, serverGroups } = params;
  const stored = await db.getCaughtUpGroups();
  if (serverGroups === void 0) {
    if (stored.length > 0) throw new Error("pull response missing groups");
    return {};
  }
  const caughtUp = serverGroups.filter(
    (g) => stored.includes(g) || params.drainedWhole.includes(g)
  );
  await db.setCaughtUpGroups(caughtUp);
  for (const g of serverGroups) {
    if (caughtUp.includes(g)) continue;
    const r = await pullGroup(params, g, (await db.getLostAt())?.[g]);
    if (r === "revoked") continue;
    if (r !== void 0) return { stop: r };
    caughtUp.push(g);
    await db.setCaughtUpGroups([...caughtUp]);
  }
  return {};
}

// src/sync-gate.ts
async function handleGateResponse(db, localEpoch, errBody) {
  if (errBody.reason === "epoch_mismatch") {
    if (errBody.localEpoch === void 0 || errBody.serverEpoch === void 0 || errBody.nodeId === void 0)
      throw new Error("epoch_mismatch response missing localEpoch/serverEpoch/nodeId");
    await db.reconcileEpoch(
      {
        serverEpoch: errBody.serverEpoch,
        localEpoch: errBody.localEpoch,
        nodeId: errBody.nodeId
      },
      localEpoch
    );
    return "retry";
  }
  if (errBody.reason === "schema_behind") return "schema_outdated";
  return null;
}
async function readGateAction(res, db, localEpoch, onGate) {
  const errBody = await res.json();
  onGate?.(errBody);
  return handleGateResponse(db, localEpoch, errBody);
}

// src/sync-pull.ts
async function pullChanges(opts, session, blocks, dropped2) {
  const { db, apiBase, author, headers, fetchFn = fetch } = opts;
  const tables = db.registeredTables();
  let cursor = session.lastPulledAt;
  const syncStartLastPulledAt = cursor;
  let serverGroups;
  let fromZero = syncStartLastPulledAt === 0;
  let drainedWhole;
  let bi = 0;
  let untilCursor;
  const fetchPage2 = (from, until) => fetchFn(`${apiBase}/pull`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...headers },
    body: JSON.stringify({
      lastPulledAt: from,
      tables,
      author,
      instanceId: session.instanceId,
      serverEpoch: session.serverEpoch,
      appVersion: opts.appVersion,
      ...opts.pullPageSize ? { limit: opts.pullPageSize } : {},
      ...until !== void 0 ? { untilCursor: until } : {}
    })
  });
  let prefetch;
  const armPrefetch = (from, until) => {
    const res = fetchPage2(from, until);
    res.catch(() => {
    });
    prefetch = { from, until, res };
  };
  const discardPrefetch = () => {
    prefetch?.res.then(
      (r) => {
        r.body?.cancel().catch(() => {
        });
      },
      () => {
      }
    );
    prefetch = void 0;
  };
  const advance = async (jumpCurrent) => {
    discardPrefetch();
    if (jumpCurrent && bi < blocks.length) {
      const b = blocks[bi];
      if (b.post > cursor) {
        cursor = b.post;
        await db.setLastPulledAt(cursor);
      }
      bi += 1;
    }
    for (; ; ) {
      if (bi >= blocks.length) {
        untilCursor = void 0;
        return;
      }
      const b = blocks[bi];
      if (b.post <= cursor) {
        bi += 1;
        continue;
      }
      if (cursor < b.pre) {
        untilCursor = b.pre;
        return;
      }
      cursor = b.post;
      await db.setLastPulledAt(cursor);
      bi += 1;
    }
  };
  await advance(false);
  let hasMore = false;
  let triggerEvaluated = false;
  let owed;
  let purgeMark;
  const pullStart = Date.now();
  let pullHttpMs = 0;
  let pullApplyMs = 0;
  try {
    do {
      const pullHttpStart = Date.now();
      const armed = prefetch;
      const usable = armed !== void 0 && armed.from === cursor && armed.until === untilCursor;
      if (!usable) discardPrefetch();
      const pullRes = await (usable ? armed.res : fetchPage2(cursor, untilCursor));
      prefetch = void 0;
      if (pullRes.status === 409) {
        const action = await readGateAction(
          pullRes,
          db,
          session.epoch.localEpoch,
          opts.migration?.onGate
        );
        if (action === "retry") return { kind: "retry" };
        if (action === "schema_outdated") return { kind: "stop", cause: "schema_behind" };
      }
      if (pullRes.status === 503 || pullRes.status === 425)
        return { kind: "stop", cause: "not_ready" };
      if (pullRes.status === 403) {
        const code = readForbidden(await pullRes.text())?.code;
        if (code !== void 0) throw new AccountRefusedError("pull", code);
      }
      if (!pullRes.ok) throw new Error(`Pull failed: ${pullRes.status}`);
      const page = await pullRes.json();
      pullHttpMs += Date.now() - pullHttpStart;
      serverGroups = page.groups;
      if (fromZero) {
        const named = page.groups ?? [];
        drainedWhole = drainedWhole === void 0 ? named : drainedWhole.filter((g) => named.includes(g));
      }
      if (typeof page.maxPurgedCursor !== "number")
        throw new Error("pull response missing maxPurgedCursor");
      if (!triggerEvaluated) {
        triggerEvaluated = true;
        owed = await db.getRepullSince();
        const watermark = Math.min(
          ...syncStartLastPulledAt > 0 ? [syncStartLastPulledAt] : [],
          ...owed !== void 0 ? [owed] : [],
          ...page.groups !== void 0 ? await lostAtOf.call(db, page.groups) : []
        );
        if (watermark < page.maxPurgedCursor) {
          discardPrefetch();
          const wiped = await db.wipeAndResetForRepull();
          if (wiped) {
            owed = void 0;
            await db.clearRepullSince();
            cursor = 0;
            untilCursor = void 0;
            bi = blocks.length;
            hasMore = true;
            fromZero = true;
            drainedWhole = void 0;
            serverGroups = void 0;
            continue;
          }
          await db.markRepullSince(watermark);
          owed = void 0;
        } else if (Number.isFinite(watermark)) purgeMark = page.maxPurgedCursor;
      } else if (purgeMark !== void 0 && page.maxPurgedCursor > Math.max(purgeMark, cursor)) {
        await db.markRepullSince(cursor);
        owed = void 0;
        purgeMark = void 0;
      }
      if (page.hasMore === true) armPrefetch(page.timestamp, untilCursor);
      const pullApplyStart = Date.now();
      await db.applyPull(page.changes);
      pullApplyMs += Date.now() - pullApplyStart;
      cursor = page.timestamp;
      if (cursor > 0) await db.advancePullCursor(cursor, page.groups, drainedWhole ?? []);
      if (page.hasMore === void 0) throw new Error("pull response missing hasMore");
      hasMore = page.hasMore;
      if (!hasMore && untilCursor !== void 0) {
        await advance(true);
        hasMore = true;
      }
    } while (hasMore);
  } finally {
    discardPrefetch();
  }
  const pullMs = Date.now() - pullStart;
  if (owed !== void 0) await db.clearRepullSince();
  const backfillStart = Date.now();
  const outcome = await runGroupBackfill({
    db,
    serverGroups,
    drainedWhole: drainedWhole ?? [],
    pullFetch: (body) => fetchFn(`${apiBase}/pull`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...headers },
      body: JSON.stringify({
        tables,
        author,
        instanceId: session.instanceId,
        serverEpoch: session.serverEpoch,
        appVersion: opts.appVersion,
        ...opts.pullPageSize ? { limit: opts.pullPageSize } : {},
        ...body
      })
    }),
    readGate: (res) => readGateAction(res, db, session.epoch.localEpoch, opts.migration?.onGate)
  });
  const backfillMs = Date.now() - backfillStart;
  if (outcome.stop === "retry") return { kind: "retry" };
  if (outcome.stop === "schema_outdated") return { kind: "stop", cause: "schema_behind" };
  if (outcome.stop === "residency") return { kind: "stop", cause: "not_ready" };
  for (const { table: table2, id } of dropped2)
    if (await db.table(table2).get(id) === void 0) await db.markDroppedRowGone(table2, id);
  return { kind: "done", timing: { pullMs, pullHttpMs, pullApplyMs, backfillMs } };
}

// src/sync-push.ts
import { hexCompare as hexCompare3, UNWRITTEN_FIELD as UNWRITTEN_FIELD2 } from "@jbroll/rowboat-shared";

// src/sync-quarantine.ts
function requireCreateId(entry) {
  if (entry.createId === void 0)
    throw new Error("quarantine: create side-log entry read back without its key");
  return entry.createId;
}
function createRef(createId, entry) {
  return { createId, stamp: entry.stamp };
}
function knownReason(r) {
  const { reason } = r;
  const known = QUARANTINE_REASONS.find((k) => k === reason);
  if (known !== void 0) return known;
  throw new Error(
    `push rejection of ${r.table}/${r.id} has unknown reason ${JSON.stringify(reason)} \u2014 protocol error`
  );
}
function requireHash(r) {
  if (typeof r.hash !== "string" || r.hash === "")
    throw new Error(
      `push rejection of ${r.table}/${r.id} (blob_missing) names no hash \u2014 protocol error`
    );
  return r.hash;
}
function opColumns2(ops) {
  const out = [];
  for (const op of ops) {
    if (op.isDelete === 1) {
      out.push("__deleted");
      continue;
    }
    for (const k of Object.keys(JSON.parse(op.changes)))
      if (k !== "__clocks" && !out.includes(k)) out.push(k);
  }
  return out;
}
function requireStoredGroup(r) {
  if (r.storedGroup === void 0)
    throw new Error(
      `push rejection of ${r.table}/${r.id} (${r.reason}) names no storedGroup \u2014 protocol error`
    );
  return r.storedGroup;
}
async function reconcileRejectedRows(opts, work, rejected) {
  const { db } = opts;
  const quarantine = [];
  const discarded = [];
  const repull = /* @__PURE__ */ new Set();
  let repullAll = false;
  let resetCursor = false;
  const outcome = () => ({
    quarantine,
    dropped: discarded,
    repull: repullAll ? "all" : [...repull],
    resetCursor
  });
  if (rejected.length === 0) return outcome();
  const { author } = opts;
  const pendingCreate = /* @__PURE__ */ new Map();
  for (const entry of work.entries) {
    const createId = requireCreateId(entry);
    for (const id of entry.ids)
      pendingCreate.set(rowKey2(entry.table, id), createRef(createId, entry));
  }
  const createdRow = /* @__PURE__ */ new Map();
  for (const [table2, rows] of work.creates)
    for (const r of rows) createdRow.set(rowKey2(table2, r.id), r);
  const opsByRow = /* @__PURE__ */ new Map();
  for (const op of work.ops) {
    const key = rowKey2(op.table, op.id);
    const found = opsByRow.get(key);
    if (found) found.push(op);
    else opsByRow.set(key, [op]);
  }
  const pushedHere = /* @__PURE__ */ new Set([...pendingCreate.keys(), ...createdRow.keys(), ...opsByRow.keys()]);
  for (const r of rejected)
    if (!pushedHere.has(rowKey2(r.table, r.id)))
      throw new Error(
        `push rejection of ${r.table}/${r.id} names a row this push did not send \u2014 protocol error`
      );
  const at = Date.now();
  for (const r of rejected) {
    const key = rowKey2(r.table, r.id);
    const row = createdRow.get(key);
    const pc = pendingCreate.get(key);
    const work2 = {
      table: r.table,
      id: r.id,
      ops: opsByRow.get(key) ?? [],
      // A row the side-log named but that is no longer a pending create (an echo replaced it) has no
      // create to restore, so it counts as acknowledged.
      ...pc !== void 0 && row !== void 0 ? { pendingCreate: pc } : {}
    };
    const reason = knownReason(r);
    const entry = {
      ...work2,
      reason,
      at,
      ...reason === "blob_missing" ? { hash: requireHash(r) } : {}
    };
    const foreignGroup = entry.reason === "unknown_group" || entry.reason === "unauthorized_group";
    const column = db.scopeCol(r.table);
    if (column === void 0) {
      quarantine.push(entry);
      continue;
    }
    if (entry.pendingCreate === void 0) {
      if (!foreignGroup) {
        quarantine.push(entry);
        continue;
      }
      const stored = requireStoredGroup(r);
      const columns = opColumns2(entry.ops);
      if (columns.length === 0) continue;
      discarded.push({
        table: r.table,
        id: r.id,
        reason: entry.reason,
        ...r.group !== void 0 ? { group: r.group } : {},
        columns,
        at
      });
      if (stored === null) {
        repullAll = true;
        resetCursor = true;
      } else {
        repull.add(stored);
      }
      continue;
    }
    const orphan = foreignGroup || entry.reason === "invalid_scope";
    if (orphan && author !== void 0) entry.suggestedGroup = author;
    quarantine.push(entry);
  }
  return outcome();
}
function reportRefused(o) {
  if (o.dropped.length > 0)
    console.warn(
      `[rowboat] discarded ${o.dropped.length} refused change(s) that can never be accepted: ` + o.dropped.map((d) => `${d.table}/${d.id} [${d.columns.join(", ")}]`).join(", ")
    );
  if (o.quarantine.length > 0)
    console.warn(
      `[rowboat] quarantined ${o.quarantine.length} refused row(s): ` + o.quarantine.map((e) => `${e.table}/${e.id} (${e.reason})`).join(", ")
    );
}

// src/sync-push.ts
async function gzipIfAvailable(body) {
  if (typeof CompressionStream === "undefined") return { data: body };
  const cs = new CompressionStream("gzip");
  const writer = cs.writable.getWriter();
  writer.write(new TextEncoder().encode(body));
  writer.close();
  return { data: await new Response(cs.readable).arrayBuffer(), encoding: "gzip" };
}
function opToWireRecord(op) {
  const changes = JSON.parse(op.changes);
  return {
    id: op.id,
    __hlc: op.hlc,
    ...changes,
    __op: "update",
    // Carry the op's BIRTH origin for counter dedup (set only on counter ops). The server requires it
    // whenever a counter column is present; a lost-ack re-send under a new tab keeps the ORIGINAL origin.
    ...op.origin !== void 0 ? { __origin: op.origin } : {}
  };
}
function movesRow(db, op) {
  const column = db.scopeCol(op.table);
  return column !== void 0 && op.isDelete !== 1 && column in JSON.parse(op.changes);
}
function requireKey(key, what) {
  if (key === void 0) throw new Error(`${what} read back without its auto-increment key`);
  return key;
}
async function pushChunk(opts, session, backlog) {
  const { db, apiBase, author, headers, fetchFn = fetch } = opts;
  const timing = { buildMs: 0, gzipMs: 0, pushMs: 0, pushHttpMs: 0, pushPostMs: 0 };
  const buildStart = Date.now();
  const budget = opts.pushChunkRows ?? PUSH_CHUNK_ROWS;
  let pendingEntries;
  let pendingOps2;
  if (backlog) {
    pendingEntries = backlog.entries;
    pendingOps2 = backlog.ops;
  } else {
    const [entries, ops2] = await Promise.all([db.pendingCreateEntries(), db.pendingOps()]);
    pendingEntries = await db.splitCreateEntries(entries, ops2, budget);
    pendingOps2 = ops2;
  }
  const plan = planPushChunk(
    pendingEntries.map((e) => ({
      createId: requireKey(e.createId, "create side-log entry"),
      table: e.table,
      stamp: e.stamp,
      ids: e.ids
    })),
    pendingOps2.map((o) => ({
      opId: requireKey(o.opId, "pending op"),
      table: o.table,
      id: o.id,
      hlc: o.hlc
    })),
    budget
  );
  const inChunk = (e) => plan.createIds.has(requireKey(e.createId, "create side-log entry"));
  const opInChunk = (o) => plan.opIds.has(requireKey(o.opId, "pending op"));
  const remaining = {
    entries: pendingEntries.filter((e) => !inChunk(e)),
    ops: pendingOps2.filter((o) => !opInChunk(o))
  };
  const chunkEntries = pendingEntries.filter(inChunk);
  const allOps = pendingOps2.filter(opInChunk);
  const { creates, createIds } = await db.pendingCreates(chunkEntries);
  const kinds = {};
  for (const op of allOps) {
    if (!(op.table in kinds))
      kinds[op.table] = { counters: db.counterCols(op.table), sets: db.setCols(op.table) };
  }
  const ops = allOps.length > 0 ? coalescePendingOps(allOps, creates, kinds) : [];
  if (allOps.length > 0 && ops.length === 0)
    throw new Error(`coalescePendingOps returned no ops for ${allOps.length} pending ops`);
  const changesPayload = {};
  for (const [table2, rows] of creates) {
    changesPayload[table2] ??= { upserted: [] };
    const setColSet = db.setCols(table2);
    const counterColSet = db.counterCols(table2);
    for (const row of rows) {
      const hlcHex = row.__localHlc;
      const { __localHlc: _lh, __deleted: _del, ...rest } = row;
      delete rest[UNWRITTEN_FIELD2];
      for (const col of db.unwrittenAtDefault(table2, row)) delete rest[col];
      for (const col of setColSet) {
        if (col in rest)
          rest[col] = { add: JSON.parse(rest[col]) };
      }
      for (const col of counterColSet) delete rest[col];
      changesPayload[table2].upserted.push({
        ...rest,
        __hlc: hlcHex,
        __op: "create"
      });
    }
  }
  const deletedIds = /* @__PURE__ */ new Map();
  for (const op of ops) {
    changesPayload[op.table] ??= { upserted: [] };
    if (op.isDelete) {
      changesPayload[op.table].upserted.push({ id: op.id, __hlc: op.hlc, __op: "remove" });
      let byId = deletedIds.get(op.table);
      if (byId === void 0) {
        byId = /* @__PURE__ */ new Map();
        deletedIds.set(op.table, byId);
      }
      const prev = byId.get(op.id);
      if (prev === void 0 || op.hlc > prev) byId.set(op.id, op.hlc);
    } else {
      changesPayload[op.table].upserted.push(opToWireRecord(op));
    }
  }
  for (const table2 of Object.keys(changesPayload)) {
    const upserted = changesPayload[table2].upserted;
    const seen = /* @__PURE__ */ new Set();
    let hasSameRow = false;
    for (const r of upserted) {
      const id = r.id;
      if (seen.has(id)) {
        hasSameRow = true;
        break;
      }
      seen.add(id);
    }
    if (hasSameRow) upserted.sort((a, b) => hexCompare3(a.__hlc, b.__hlc));
  }
  timing.buildMs += Date.now() - buildStart;
  let drained = false;
  let block;
  let dropped2 = [];
  if (Object.keys(changesPayload).length > 0) {
    const pushStart = Date.now();
    const echoMark = await db.getRebasedEchoHlc();
    const forceEcho = allOps.some((o) => movesRow(db, o)) || echoMark !== void 0 && (allOps.some((o) => o.isDelete !== 1 && o.hlc <= echoMark) || [...creates.values()].some(
      (rows) => rows.some((r) => r.__localHlc <= echoMark)
    ));
    const rawPushBody = JSON.stringify({
      author,
      instanceId: session.instanceId,
      serverEpoch: session.serverEpoch,
      appVersion: opts.appVersion,
      lastPulledAt: forceEcho ? 0 : session.lastPulledAt,
      changes: changesPayload
    });
    const gzipStart = Date.now();
    const { data: pushBody, encoding: pushEncoding } = await gzipIfAvailable(rawPushBody);
    timing.gzipMs += Date.now() - gzipStart;
    const pushHttpStart = Date.now();
    const pushRes = await fetchFn(`${apiBase}/sync`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...pushEncoding ? { "Content-Encoding": pushEncoding } : {},
        ...headers
      },
      body: pushBody
    });
    if (pushRes.status === 409) {
      const action = await readGateAction(
        pushRes,
        db,
        session.epoch.localEpoch,
        opts.migration?.onGate
      );
      if (action === "retry") return { kind: "retry" };
      if (action === "schema_outdated") return { kind: "stop", cause: "schema_behind" };
    }
    if (pushRes.status === 503 || pushRes.status === 425)
      return { kind: "stop", cause: "not_ready" };
    if (pushRes.status === 403) {
      const refusal = readForbidden(await pushRes.text());
      if (refusal?.code !== void 0) throw new AccountRefusedError("push", refusal.code);
      throw new Error(`Push failed: ${pushRes.status}`);
    }
    if (pushRes.status === 413) {
      const raw = await pushRes.text();
      let limit;
      try {
        limit = JSON.parse(raw).limit;
      } catch {
        limit = void 0;
      }
      if (typeof limit !== "number") throw new Error(`Push failed: 413 ${raw.slice(0, 200)}`);
      throw oversizedPushError(changesPayload, limit, budget);
    }
    if (!pushRes.ok) throw new Error(`Push failed: ${pushRes.status}`);
    const pushData = await pushRes.json();
    if (pushData.serverCursor === void 0 || pushData.prePushCursor === void 0)
      throw new Error("Push response missing serverCursor/prePushCursor");
    if (pushData.echo === void 0) throw new Error("Push response missing echo");
    const echo = pushData.echo;
    if (!Array.isArray(pushData.rejected)) throw new Error("Push response missing rejected");
    const rejected = pushData.rejected;
    const rejectedKeys = new Set(rejected.map((r) => rowKey2(r.table, r.id)));
    block = pushData.serverCursor > pushData.prePushCursor ? { pre: pushData.prePushCursor, post: pushData.serverCursor } : void 0;
    timing.pushHttpMs += Date.now() - pushHttpStart;
    const pushPostStart = Date.now();
    const opIds = allOps.map((o) => requireKey(o.opId, "pending op"));
    const createRows = /* @__PURE__ */ new Set();
    if (Object.keys(echo).length > 0)
      for (const [table2, rows] of creates)
        for (const r of rows) createRows.add(rowKey2(table2, r.id));
    const work = { entries: chunkEntries, ops: allOps, creates };
    const refused2 = await reconcileRejectedRows(opts, work, rejected);
    await db.applyEcho(echo, { opIds: new Set(opIds), createRows, createIds, refused: refused2 });
    reportRefused(refused2);
    dropped2 = refused2.dropped.map((d) => ({ table: d.table, id: d.id }));
    if (rejectedKeys.size > 0) {
      for (const [table2, byId] of deletedIds) {
        for (const id of [...byId.keys()]) if (rejectedKeys.has(rowKey2(table2, id))) byId.delete(id);
        if (byId.size === 0) deletedIds.delete(table2);
      }
    }
    if (deletedIds.size > 0) {
      let pushedHighWater = "";
      for (const e of chunkEntries) if (e.stamp > pushedHighWater) pushedHighWater = e.stamp;
      for (const o of allOps) if (o.hlc > pushedHighWater) pushedHighWater = o.hlc;
      for (const [table2, byId] of deletedIds) {
        const echoed = echo[table2];
        for (const r of echoed === void 0 ? [] : echoed.upserted) {
          const id = r.id;
          if (typeof id !== "string")
            throw new Error(`push echo: ${table2} record has no id \u2014 protocol error`);
          if (r.__op === "remove") {
            const prev = byId.get(id);
            if (prev !== void 0) byId.set(id, pushedHighWater > prev ? pushedHighWater : prev);
          } else byId.delete(id);
        }
        if (byId.size === 0) deletedIds.delete(table2);
      }
      await db.hardDeleteAcked(deletedIds);
    }
    drained = allOps.length > 0 || createIds.length > 0;
    timing.pushPostMs += Date.now() - pushPostStart;
    timing.pushMs += Date.now() - pushStart;
  } else if (createIds.length > 0) {
    await db.clearCreates(createIds);
    drained = true;
  }
  return { kind: "chunk", hasMore: plan.hasMore, drained, block, timing, remaining, dropped: dropped2 };
}

// src/sync.ts
async function syncWithServer(opts, retryDepth = 0) {
  const name = await opts.db.syncLockName();
  await withSyncLock(name, "syncWithServer", () => syncRound(opts, retryDepth));
}
async function syncRound(opts, retryDepth) {
  const { db } = opts;
  let depth = retryDepth;
  for (; ; ) {
    if (depth > 5) throw new Error("syncWithServer: too many 409 gate retries");
    const roundStartCursor = await db.getLastPulledAt();
    let lastPulledAt = roundStartCursor;
    const cursorFloor = opts.migration?.cursorFloor?.();
    if (cursorFloor !== void 0 && cursorFloor > lastPulledAt) lastPulledAt = cursorFloor;
    const epoch = await db.getServerEpoch();
    const session = {
      epoch,
      serverEpoch: opts.serverEpoch ?? epoch.serverEpoch,
      instanceId: await db.getInstanceId(),
      lastPulledAt
    };
    const blocks = [];
    const dropped2 = [];
    const pushTiming = {
      buildMs: 0,
      gzipMs: 0,
      pushMs: 0,
      pushHttpMs: 0,
      pushPostMs: 0
    };
    let restart = false;
    let stopped;
    let backlog;
    for (; ; ) {
      const outcome = await pushChunk(opts, session, backlog);
      if (outcome.kind === "stop") {
        stopped = outcome.cause;
        break;
      }
      if (outcome.kind === "retry") {
        restart = true;
        break;
      }
      backlog = outcome.remaining;
      if (outcome.block) blocks.push(outcome.block);
      dropped2.push(...outcome.dropped);
      for (const k of Object.keys(pushTiming))
        pushTiming[k] += outcome.timing[k];
      if (!outcome.hasMore) break;
      if (!outcome.drained)
        throw new Error("syncWithServer: a push chunk drained no work with a backlog remaining");
    }
    if (stopped !== void 0) {
      opts.onStopped?.(stopped);
      return;
    }
    if (restart) {
      depth += 1;
      continue;
    }
    const storedCursor = await db.getLastPulledAt();
    const pullSession = storedCursor < roundStartCursor ? { ...session, lastPulledAt: storedCursor } : session;
    const pull = await pullChanges(opts, pullSession, blocks, dropped2);
    if (pull.kind === "retry") {
      depth += 1;
      continue;
    }
    if (pull.kind === "stop") {
      opts.onStopped?.(pull.cause);
      return;
    }
    opts.onTiming?.({ ...pushTiming, ...pull.timing });
    return;
  }
}

// src/sync-nudge.ts
function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
function startNudge(onNudge, opts) {
  const fetchFn = opts.fetchFn ?? fetch;
  const controller = new AbortController();
  const signal = controller.signal;
  let closed = false;
  let queued = false;
  let attempts = 0;
  const schedule = () => {
    if (closed || queued) return;
    queued = true;
    setTimeout(() => {
      queued = false;
      if (!closed) onNudge();
    }, 0);
  };
  const readStream = async () => {
    const res = await fetchFn(opts.url, { headers: { Accept: "text/event-stream" }, signal });
    if (!res.ok) throw new Error(`nudge subscribe failed with status ${res.status}`);
    if (res.body === null) throw new Error("nudge subscribe returned no body");
    attempts = 0;
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (; ; ) {
      const { done, value } = await reader.read();
      if (done) return;
      buffer += decoder.decode(value, { stream: true });
      const parts = buffer.split(/\r?\n\r?\n/);
      buffer = parts.pop() ?? "";
      for (const frame of parts) {
        const eventLine = frame.split(/\r?\n/).find((line) => line.startsWith("event:"));
        if (eventLine?.slice("event:".length).trim() === "nudge") schedule();
      }
      if (closed) return;
    }
  };
  const close2 = () => {
    closed = true;
    controller.abort();
    opts.signal?.removeEventListener("abort", close2);
  };
  if (opts.signal?.aborted) close2();
  else opts.signal?.addEventListener("abort", close2);
  void (async () => {
    while (!closed) {
      try {
        await readStream();
        throw new Error("nudge stream ended");
      } catch (err) {
        if (closed) return;
        attempts += 1;
        opts.onError?.(err);
        await sleep(Math.min(100 * 2 ** attempts, 5e3));
      }
    }
  })();
  return { close: close2 };
}
export {
  ANON_IDENTITY,
  ANON_LOCK_TIMEOUT_MS,
  AccountRefusedError,
  AnonStoreBusyError,
  adoptAnonStore,
  adoptRows,
  buildRowboatDb,
  createBlobClient,
  createBlobUploadQueue,
  createOfflineBlobStore,
  dexieQuery,
  fracKey,
  indexedDbQueueStore,
  matchesPredicate,
  mediaCell,
  memoryQueueStore,
  orderedList,
  orderedToArray,
  parseMediaCell,
  resumeAdopt,
  startNudge,
  storeName,
  syncWithServer,
  useRow,
  useRows
};
